import { assertNever } from '@dadohr/shared/domainTypes/00-assertNever';
import { addressToText } from '@dadohr/shared/domainTypes/address';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  Audience,
  AutomationAction,
  AutomationActionDenormalized,
  AutomationActionStatus,
  AutomationActionTemplate,
  AutomationActionType,
  denormalizedActionDisplayStatus,
  denormalizedAutomationDisplayStatus,
  AutomationStatus,
  ConcreteExperienceMessage,
  ContentTemplate,
  ContentType,
  CreateMeetingServiceType,
  EMPLOYEE_APP_REROUTER_HINT_QUERY_PARAM,
  EmployeeAppRerouterHints,
  EventChannel,
  ExperienceActor,
  ExperienceActorType,
  ExperienceAnchorType,
  ExperienceEventType,
  ExperienceStatus,
  ExperienceTemplateMessage,
  ExperienceUserActor,
  getSubTaskTemplates,
  IAbstractSubTaskDenormalized,
  IAutomation,
  IAutomationDenormalized,
  IAutomationTemplate,
  IExperience,
  IExperienceContact,
  IExperienceDenormalized,
  IExperienceEvent,
  IExperienceEventSubTaskDone,
  IExperienceEventTaskDone,
  IExperienceMessageDenormalized,
  IExperienceTemplate,
  isContentTemplateBlock,
  isEventDrivenTiming,
  isLayoutTemplateBlock,
  isSubTaskTemplateBlock,
  ISubTask,
  ISubtaskCreateMeetingDenormalized,
  ISubtaskCreateMeetingTemplate,
  ISubTaskQuestionTemplate,
  ITask,
  ITaskDenormalized,
  ITaskTemplate,
  LayoutBlock,
  LayoutBlockType,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY,
  QuestionType,
  RequirementType,
  ResendExperienceItemArgs,
  RetryAssigningSupportingActorItemArgs,
  SubTaskDenormalized,
  SubTaskQuestionAnswer,
  SubTaskStatus,
  SubTaskTemplate,
  SubTaskType,
  TaskBlockDenormalized,
  TaskStatus,
  TaskTemplateCompletionCritertia
} from '@dadohr/shared/domainTypes/experience';
import { formatScaleAnswer } from '@dadohr/shared/domainTypes/questionScale';

import { ErroDataType } from '@dadohr/shared/domainTypes/instance';
import { CalendlyEventDetails } from '@dadohr/shared/domainTypes/integrations/calendly';
import {
  CUSTOMER_ACTIONS,
  InstanceDeletedAction
} from '@dadohr/shared/domainTypes/internalAlerts';
import { CustomUserField, IOrganization } from '@dadohr/shared/domainTypes/org';
import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { CF, FS } from '@dadohr/shared/versions';
import { validate as isValidEmail } from 'email-validator';
import {
  compact,
  findLast,
  fromPairs,
  get,
  keyBy,
  mapValues,
  minBy,
  sortBy,
  uniqBy
} from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import shortid from 'shortid';
import ENV from '../../env.json';
import { markMatchingAssigneeBouncesIgnored } from './clearAssigneeBounce';
import { toEventDoc } from '../../features/Events/services/document';

import { readSyncConfig } from '../../features/Experiences/components/Builders/AutomationBuilder/ActionSettings/SyncToDataDestination/helpers';
import { isReplacementToken } from '../../features/Experiences/components/Builders/TaskBuilder/validation';
import { ExperienceProblemData } from '../../features/Experiences/pages/Details/InstanceProblems';
import {
  getAllByIdAsMap,
  LoadingValue,
  removeDoc,
  store,
  updateDataInDoc,
  updateInTransaction,
  useCombineLoadingValues,
  useCombineLoadingValues3,
  useMappedLoadingValue
} from '../firebase';
import { callFirebaseFunction } from '../firebaseFunctions';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from '../firecache/collectionListener';
import {
  createDocumentListenerGetter,
  useDocumentListener,
  useDocumentListeners
} from '../firecache/documentListener';
import { tryReportCustomerAction } from '../internalAlerts';
import { maskAddress, maskString } from '../mask';
import { toDenormalizedOrg } from '../orgs';
import {
  ExperienceReplacements,
  getSalutation,
  interpolateExperienceText,
  InterpolationDefaultOptions,
  InterpolationVariable,
  userToInterpolation
} from '../set/interpolation';
import {
  addToTs,
  fromMoment,
  MomentRange,
  now,
  ONE_DAY,
  ONE_MINUTE,
  toMoment,
  tsFromMillis,
  tsIsAfter
} from '../time';
import {
  getUserValue,
  toDenormalizedUser,
  toFullName,
  UNKNOWN_USER,
  useUserMapById
} from '../user';
import {
  filterBlocksForAudience,
  getAudienceById,
  getAudienceForUser
} from './audience';
import {
  normalizeExperience,
  toExperienceDoc,
  toSubTaskQuestionAnswerDoc
} from './documents';
import {
  __recalculateExperienceInstanceAtPointInTimeWithoutSideEffects,
  computeAutomationStatus,
  computeExpectedEndForInstance,
  computeTaskStatus,
  computeTaskStatusButKeepNotStartedState,
  InstanceRecalculateReason
} from './recalcualateInstance';
import {
  createNotStartedMessageStatus,
  createNotStartedTask,
  getAllMessageTemplates,
  getTaskTemplateById,
  skipAllBecauseCatchupModeIsDisabled
} from './template';
import {
  getAnchorOffsetForExperience,
  getFixedTimingOrApproximateParentTiming,
  getFreshNonTaskStartDate,
  getFreshTaskDates,
  isStandardTimingForAutomation,
  isStandardTimingForMessage,
  isStandardTimingForTask
} from './timing';

const experienceInstanceCollection = () => store().collection(FS.experiences);

const DELIMITER = '@@@';

const experienceInstanceStoreByUserId = createCollectionListenerStore((key) => {
  const [orgId, userId] = key.split(DELIMITER);
  return new CollectionListener(
    experienceInstanceCollection()
      .where('orgId', '==', orgId)
      .where('userIds', 'array-contains', userId),
    toExperienceDoc
  );
});

export const useExperienceInstancesByUserId = (orgId: string, userId: string) =>
  useCollectionListener(
    experienceInstanceStoreByUserId([orgId, userId].join(DELIMITER))
  );

const getExperienceInstanceStoreByTemplateId = createCollectionListenerStore(
  (key) => {
    const [orgId, tplId] = key.split(DELIMITER);
    return new CollectionListener(
      experienceInstanceCollection()
        .where('orgId', '==', orgId)
        .where('templateId', '==', tplId),
      toExperienceDoc
    );
  }
);

export const toExpInstancesByTplIdInRangeKey = (
  orgId: string,
  tplId: string,
  range: MomentRange
) => {
  return [
    orgId,
    tplId,
    `${range.start.valueOf()}`,
    `${range.end.valueOf()}`
  ].join(DELIMITER);
};

/**
 * @deprecated Use useExperienceInstancesCurrentlyRelevant instead
 */
export const getExperienceInstanceStoreByTemplateIdInRange = createCollectionListenerStore(
  (key) => {
    const [orgId, tplId, start] = key.split(DELIMITER);
    return new CollectionListener(
      experienceInstanceCollection()
        .where('orgId', '==', orgId)
        .where('templateId', '==', tplId)
        .where('startsAt', '>=', tsFromMillis(parseInt(start, 10))),
      // Ignoring end for now - we have cases where participants where moved far into the future
      // and we currently provide no other way of finding these participants anymore, if we use a proper time range
      // Once we have better tools to show people in such a distant future, activate the range the again, so
      // that consumers of this fn can truly expect a view on "currently relevant" instances
      // .where('startsAt', '<=', tsFromMillis(parseInt(end, 10))),
      toExperienceDoc
    );
  }
);

/**
 * @deprecated Use useExperienceInstancesCurrentlyRelevant instead
 */
export const useExperienceInstancesByTemplateIdInRange = (
  orgId: string,
  tplId: string,
  range: MomentRange
) => {
  const key = toExpInstancesByTplIdInRangeKey(orgId, tplId, range);
  return useCollectionListener(
    getExperienceInstanceStoreByTemplateIdInRange(key)
  );
};

export const getUpcomingExperienceInstanceStoreByTemplateIdByMinStart = createCollectionListenerStore(
  (key) => {
    const [orgId, tplId, start] = key.split(DELIMITER);
    return new CollectionListener(
      experienceInstanceCollection()
        .where('orgId', '==', orgId)
        .where('templateId', '==', tplId)
        .where('status', '==', ExperienceStatus.NOT_STARTED)
        .where('startsAt', '<=', tsFromMillis(parseInt(start, 10))),
      toExperienceDoc
    );
  }
);

export const getOngoingExperienceInstanceStoreByTemplateIdByMaxEnd = createCollectionListenerStore(
  (key) => {
    const [orgId, tplId, end] = key.split(DELIMITER);
    return new CollectionListener(
      experienceInstanceCollection()
        .where('orgId', '==', orgId)
        .where('templateId', '==', tplId)
        .where('status', 'in', [
          ExperienceStatus.RUNNING,
          ExperienceStatus.LATE,
          ExperienceStatus.COMPLETE
        ])
        .where('expectedEnd', '>', tsFromMillis(parseInt(end, 10))),
      toExperienceDoc
    );
  }
);

/**
 * Companion to getOngoingExperienceInstanceStoreByTemplateIdByMaxEnd for the
 * `createdAt` leg of the BE archival rule. The BE keeps an instance relevant
 * while `now <= max(expectedEnd, createdAt) + expectedEndThreshold`; Firestore
 * allows an inequality on only ONE field, so the maxEnd store covers the
 * `expectedEnd` leg and this store covers the `createdAt` leg. That's what
 * surfaces LATE / BACKDATED enrollments — a recent `createdAt` but an
 * `expectedEnd` already in the past, which the maxEnd query never fetches.
 * Requires the (orgId, status, templateId, createdAt) composite index.
 */
export const getOngoingExperienceInstanceStoreByTemplateIdByMinCreatedAt = createCollectionListenerStore(
  (key) => {
    const [orgId, tplId, createdAtFrom] = key.split(DELIMITER);
    return new CollectionListener(
      experienceInstanceCollection()
        .where('orgId', '==', orgId)
        .where('templateId', '==', tplId)
        .where('status', 'in', [
          ExperienceStatus.RUNNING,
          ExperienceStatus.LATE,
          ExperienceStatus.COMPLETE
        ])
        .where('createdAt', '>', tsFromMillis(parseInt(createdAtFrom, 10))),
      toExperienceDoc
    );
  }
);

/**
 * FE mirror of the BE single source of truth for archival
 * (packages/shared/src/services/experience/archived.ts → getComputedArchiveDate):
 * the effective time-based archive date = expectedEndThreshold added to the
 * LATER of the instance's `expectedEnd` and its `createdAt` (enrollAt).
 *
 * The `max(expectedEnd, createdAt)` base is the very-late-enrollment fix: a
 * backdated anchor/startsAt puts `expectedEnd` in the past, so a naive
 * `expectedEnd + threshold` would classify the instance archived the instant
 * it's created. Counting the grace window from `createdAt` when
 * `createdAt > expectedEnd` gives it a real window to run first. This differs
 * from the old FE behaviour ONLY when `createdAt > expectedEnd` (a late /
 * backdated enrollment) — a normal enrollment has `createdAt <= expectedEnd`, so
 * `base === expectedEnd` and nothing changes.
 *
 * Client Firestore always rehydrates real Timestamps, so (unlike the BE) we
 * don't need the `isFirestoreTimestamp` guard; we only fall back to
 * `expectedEnd` when `createdAt` is absent on a legacy/partial doc.
 */
export const getComputedArchiveDate = (
  instance: { expectedEnd: Timestamp; createdAt?: Timestamp | null },
  expectedEndThresholdMs: number
): Timestamp => {
  const { expectedEnd, createdAt } = instance;
  const base =
    createdAt && tsIsAfter(createdAt, expectedEnd) ? createdAt : expectedEnd;
  return addToTs(base, expectedEndThresholdMs);
};

/**
 * FE mirror of the BE `isInstanceArchived`: an instance is archived (and thus
 * hidden from "currently relevant" surfaces) when EITHER `manuallyArchivedAt`
 * is set OR the time-based archive date (see getComputedArchiveDate) has passed.
 */
export const isInstanceArchived = (
  instance: {
    expectedEnd?: Timestamp | null;
    createdAt?: Timestamp | null;
    manuallyArchivedAt?: Timestamp | null;
  },
  expectedEndThresholdMs: number,
  n: Timestamp = now()
): boolean => {
  if (instance.manuallyArchivedAt) return true;
  if (!instance.expectedEnd) return false;
  return tsIsAfter(
    n,
    getComputedArchiveDate(
      { expectedEnd: instance.expectedEnd, createdAt: instance.createdAt },
      expectedEndThresholdMs
    )
  );
};

// TODO - retrieve via expectedEnd mechanic
export const useExperienceInstancesCurrentlyRelevant = (
  orgId: string,
  tplId: string,
  expectedEndThreshold: number,
  n = moment()
) => {
  const maxEnd = n
    .clone()
    .startOf('d')
    .subtract(expectedEndThreshold, 'milliseconds');
  const minStart = n.clone().startOf('d').add('3', 'weeks');
  // The `createdAt` leg of the BE archival rule (see
  // getOngoingExperienceInstanceStoreByTemplateIdByMinCreatedAt): an instance is
  // relevant while `max(expectedEnd, createdAt) + threshold > now`. `maxEnd`
  // already covers the `expectedEnd` leg; this same day-granular lower bound
  // covers the `createdAt` leg so LATE / BACKDATED enrollments (past expectedEnd,
  // recent createdAt) are fetched too. The isInstanceArchived post-filter then
  // tightens both legs to the exact bound.
  const minCreatedAt = maxEnd;

  const upcomingKey = [orgId, tplId, `${minStart.valueOf()}`].join(DELIMITER);
  const ongoingKey = [orgId, tplId, `${maxEnd.valueOf()}`].join(DELIMITER);
  const recentlyCreatedKey = [orgId, tplId, `${minCreatedAt.valueOf()}`].join(
    DELIMITER
  );
  return useMappedLoadingValue(
    useCombineLoadingValues3(
      useCollectionListener(
        getUpcomingExperienceInstanceStoreByTemplateIdByMinStart(upcomingKey)
      ),
      useCollectionListener(
        getOngoingExperienceInstanceStoreByTemplateIdByMaxEnd(ongoingKey)
      ),
      useCollectionListener(
        getOngoingExperienceInstanceStoreByTemplateIdByMinCreatedAt(
          recentlyCreatedKey
        )
      )
    ),
    ([upcoming, ongoing, recentlyCreated]) => {
      const nTs = tsFromMillis(n.valueOf());
      // The three listeners overlap (a normal ongoing instance matches both the
      // expectedEnd and createdAt legs), so dedup by id before filtering.
      return (
        uniqBy(upcoming.concat(ongoing).concat(recentlyCreated), (x) => x.id)
          // Mirror the BE archival single-source-of-truth (shared archived.ts):
          // hide an instance when it's manually archived OR past its computed
          // archive date (max(expectedEnd, createdAt) + expectedEndThreshold).
          // The minStart prefetch can also pull in already-archived short
          // experiences, so this exact filter is still required.
          .filter((x) => !isInstanceArchived(x.data, expectedEndThreshold, nTs))
      );
    }
  );
};

export const useExperienceInstancesByTemplateId = (
  orgId: string,
  tplId: string
) => {
  return useCollectionListener(
    getExperienceInstanceStoreByTemplateId([orgId, tplId].join(DELIMITER))
  );
};

const getExperienceListenerById = createDocumentListenerGetter(
  (id) => store().collection(FS.experiences).doc(id),
  toExperienceDoc
);

export const useExperienceInstancesByIds = (ids: string[]) => {
  // TODO cache
  const listeners = useMemo(() => {
    return ids.map(getExperienceListenerById);
  }, [ids]);
  return useDocumentListeners(listeners);
};

export const useExperienceInstanceById = (id: string) => {
  return useDocumentListener(getExperienceListenerById(id));
};

export const useExperienceInstanceMapByIds = (ids: string[]) => {
  return useMappedLoadingValue(
    useExperienceInstancesByIds(ids),
    (instances) => {
      return Object.fromEntries(
        ids.map((id, i) => {
          const instance = instances[i] || null;
          return [id, instance];
        })
      );
    }
  );
};

export type ExperienceStatsSimple = {
  total: number;
  done: number;
  todo: number;
  overdue: number;
  upcoming: number;

  // Trying out an alternate approach, which for now can live in parallel to the old structure
  onTrack: number;
  notOnTrack: number;
};

export const collectExperienceStatsForActor = (
  instances: Doc<IExperience>[],
  actor: string
): ExperienceStatsSimple => {
  const s: ExperienceStatsSimple = {
    total: 0,
    done: 0,
    todo: 0,
    overdue: 0,
    upcoming: 0,

    onTrack: 0,
    notOnTrack: 0
  };
  instances.forEach((instance) => {
    s.total++;
    if (instance.data.status === ExperienceStatus.NOT_STARTED) {
      s.upcoming++;
      return;
    }

    const actorTasks = instance.data.tasks.filter((t) => t.actor === actor);
    if (actorTasks.some((t) => t.status === TaskStatus.LATE)) {
      s.overdue++;
      return;
    }

    if (areAllTasksDone(actorTasks)) {
      s.done++;
      return;
    }

    if (actorTasks.some((t) => t.status === TaskStatus.RUNNING)) {
      s.todo++;
    } else {
      s.done++;
    }
  });

  instances.forEach((instance) => {
    if (instance.data.status === ExperienceStatus.NOT_STARTED) {
      // s.upcoming++;
      return;
    }
    const allTaskCount = instance.data.tasks.length;
    const lateTaskCount = instance.data.tasks.filter(
      (t) => t.status === TaskStatus.LATE
    ).length;
    if (!allTaskCount) {
      s.onTrack++;
      return;
    }
    if (lateTaskCount > 3 && lateTaskCount / allTaskCount > 0.2) {
      s.notOnTrack++;
    } else {
      s.onTrack++;
    }
  });

  return s;
};

export const useInstanceStatsByTemplateId = (
  orgId: string,
  tplId: string,
  expectedEndThreshold: number
) => {
  return useMappedLoadingValue(
    useExperienceInstancesCurrentlyRelevant(orgId, tplId, expectedEndThreshold),
    (instances) =>
      collectExperienceStatsForActor(instances, PARTICIPANT_ACTOR_KEY)
  );
};

export const generateAccessToken = () =>
  [shortid(), shortid(), shortid()].join('');

export const toStandaloneAppUrl = (
  d: Doc<IExperience>,
  actorKey: string,
  hint?: EmployeeAppRerouterHints
) => {
  const hostname = ENV.domains.standaloneApp.public;
  const isLocalhost = window.location.hostname === 'localhost';
  const u = new URL(hostname);

  const actor = d.data.actors.find((a) => a.key === actorKey);
  if (!actor) {
    console.warn('ACTOR_NOT_FOUND', { d, actorKey });
    return '';
  }
  u.pathname = `/experience/${actor.accessToken}`;
  if (isLocalhost) {
    u.searchParams.set('localhost', 'true');
  }
  if (hint) {
    u.searchParams.set(EMPLOYEE_APP_REROUTER_HINT_QUERY_PARAM, hint);
  }
  if (actorKey !== PARTICIPANT_ACTOR_KEY) {
    u.searchParams.set(
      EMPLOYEE_APP_REROUTER_HINT_QUERY_PARAM,
      EmployeeAppRerouterHints.FILTER_ALL
    );
  }
  u.searchParams.set('impersonate', 'true');
  return u.toString();
};

export const toDenormalizedContentBlock = (
  block: ContentTemplate,
  replacements: ExperienceReplacements,
  interpolationOptions: InterpolationDefaultOptions
): ContentTemplate => {
  switch (block.type) {
    case ContentType.HEADING:
      return {
        ...block,
        data: {
          ...block.data,
          t: interpolateExperienceText(
            block.data.t,
            replacements,
            interpolationOptions
          )
        }
      };
    case ContentType.TEXT:
      return {
        ...block,
        data: {
          t: {
            ...block.data.t,
            t: interpolateExperienceText(
              block.data.t.t,
              replacements,
              interpolationOptions
            )
          }
        }
      };
    case ContentType.CALLOUT:
      return {
        ...block,
        data: {
          ...block.data,
          t: {
            ...block.data.t,
            t: interpolateExperienceText(
              block.data.t.t,
              replacements,
              interpolationOptions
            )
          }
        }
      };

    case ContentType.IMAGE:
      return {
        ...block,
        data: {
          // no need to expose upload data -> always pretend to be a url type
          type: 'url',
          src: block.data.src,
          alt: interpolateExperienceText(
            block.data.alt,
            replacements,
            interpolationOptions
          ),
          align: block.data.align,
          width: block.data.width
        }
      };
    case ContentType.DIVIDER:
      return {
        ...block,
        data: {
          ...block.data
        }
      };
    default:
      return assertNever(block);
  }
};

export const toDenormalizedLayoutBlock = (block: LayoutBlock): LayoutBlock => {
  switch (block.type) {
    case LayoutBlockType.COLUMNS:
      return { ...block };
  }
};
const getAnswer = (
  sTpl: ISubTaskQuestionTemplate,
  sId: string,
  answersById: { [subTaskId: string]: Doc<SubTaskQuestionAnswer> }
): SubTaskQuestionAnswer | null => {
  const d = answersById[sId]?.data || null;
  if (
    d?.d.type === 'text' &&
    sTpl.data.question.type === 'text' &&
    sTpl.data.question.sensitive
  ) {
    return {
      ...d,
      d: {
        ...d.d,
        value: maskString(d.d.value)
      }
    };
  }
  if (
    d?.d.type === 'address' &&
    sTpl.data.question.type === 'address' &&
    sTpl.data.question.sensitive
  ) {
    return {
      ...d,
      d: {
        ...d.d,
        value: maskAddress(d.d.value)
      }
    };
  }

  return d;
};

const getMeetingDate = (
  template: ISubtaskCreateMeetingTemplate,
  tz: string,
  instanceStartDate: Timestamp,
  instanceAnchorDate: Timestamp,
  participant: Doc<IUser>
): Timestamp => {
  const bindToStartOfDayInTplTimezoneAndHandleDefault = (
    timestamp: Timestamp | undefined | null
  ): moment.Moment => {
    if (!timestamp) {
      // This should never really happen.
      // As this date is just a suggestion we can just default to something though.
      // Setting some date on the next date seems to be a fine compromise.
      return moment().tz(tz).add(1, 'day').startOf('day');
    }
    return toMoment(timestamp).tz(tz).startOf('day');
  };

  const anchor = template.data.anchor;

  //ok making this async does not seem like a good idea
  // const participantActor = actors.find((a) => a.key === PARTICIPANT_ACTOR_KEY);
  // const participant = await getUser(participantActor.userId)

  const anchorDateMoment = bindToStartOfDayInTplTimezoneAndHandleDefault(
    anchor.type === 'AFTER_MILESTONE'
      ? // This is using the trick of leaving the userfield empty, which lets us default to the instances anchorDate
        anchor.userField
        ? getUserValue(participant.data, anchor.userField, instanceAnchorDate)
        : instanceAnchorDate
      : anchor.type === 'AFTER_EXPERIENCE_START_DATE'
      ? instanceStartDate
      : // should never happen!
        // But we won't throw - this date is just a suggestion.
        // Just return a seemingly random default for it - using some point on the next day
        null
  );

  anchorDateMoment.hour(template.data.at.hour);
  anchorDateMoment.minute(template.data.at.minute);

  return addToTs(fromMoment(anchorDateMoment), anchor.lowerBoundary);
};

const CREATE_MEETING_SERVICES: {
  [type in CreateMeetingServiceType]: {
    createUrl: (
      invitees: { email: string }[],
      repeat: { enabled: boolean; frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' },
      eventDate: Timestamp,
      duration: number, // in ms
      title: string,
      description: string
    ) => string;
  };
} = {
  [CreateMeetingServiceType.GOOGLE_CALENDAR]: {
    createUrl: (invitees, repeat, eventDate, duration, title, description) => {
      // see:
      // - https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/main/services/google.md
      // - https://parcel.io/tools/calendar
      const url = new URL('https://calendar.google.com/calendar/render');
      url.searchParams.set('action', 'TEMPLATE');
      url.searchParams.set('add', invitees.map((i) => i.email).join(','));
      if (title) {
        url.searchParams.set('text', title);
      }
      if (description) {
        url.searchParams.set('details', description);
      }
      if (repeat.enabled) {
        url.searchParams.set('recur', `RRULE:FREQ=${repeat.frequency}`);
      }

      const eventDateMoment = toMoment(eventDate).utc();
      const dateFormat = 'YYYYMMDDTHHmmSS[Z]'; // the trailing Z indicates that we report the date in UTC
      url.searchParams.set(
        'dates',
        `${eventDateMoment.format(dateFormat)}/${eventDateMoment
          .clone()
          .add(duration, 'milliseconds')
          .format(dateFormat)}`
      );
      return url.toString();
    }
  },
  [CreateMeetingServiceType.MS_OUTLOOK_CALENDAR]: {
    // see:
    // - https://interactiondesignfoundation.github.io/add-event-to-calendar-docs/services/outlook-web.html
    // This link is slightly outdated - problems are described here: https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/discussions/47
    // Replacing "deeplink" with "action" seems to resolve the problem
    // This also doesn't seem to support recurring events at all :()
    createUrl: (invitees, repeat, eventDate, duration, title, description) => {
      const url = new URL(
        'https://outlook.office.com/calendar/0/action/compose'
      );
      url.searchParams.set('allday', 'false');
      url.searchParams.set('path', '/calendar/action/compose');
      url.searchParams.set('rru', 'addevent');
      if (title) {
        url.searchParams.set('subject', title);
      }
      if (description) {
        url.searchParams.set('body', description);
      }
      const startDate = toMoment(eventDate).utc();
      const endDate = startDate.clone().add(duration, 'milliseconds');
      url.searchParams.set('startdt', startDate.toISOString());
      url.searchParams.set('enddt', endDate.toISOString());
      url.searchParams.set('to', invitees.map(({ email }) => email).join(', '));

      return url.toString();
    }
  }
};

export const createCalendarEventCreationUrl = (
  type: CreateMeetingServiceType,
  d: {
    title: string;
    description?: string;
    start: Timestamp;
    duration?: number; // in ms
    repeat?: { enabled: boolean; frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' };
    invitees: { email: string }[];
  }
): string => {
  const service = CREATE_MEETING_SERVICES[type];
  return service.createUrl(
    d.invitees,
    d.repeat || { enabled: false, frequency: 'WEEKLY' },
    d.start,
    d.duration || ONE_MINUTE * 60,
    d.title,
    d.description || ''
  );
};

type CommonSubTaskProps = Omit<IAbstractSubTaskDenormalized, 'data' | 'type'>;

const findSubTaskCompletionEvent = (
  s: ISubTask,
  events: IExperienceEvent[]
) => {
  return findLast(
    events,
    (e): e is IExperienceEventSubTaskDone =>
      e.type === ExperienceEventType.SUB_TASK_DONE && e.subTaskId === s.id
  );
};

const getCommonSubTasDenormalizedProps = (
  s: ISubTask,
  events: IExperienceEvent[]
): CommonSubTaskProps => {
  return {
    id: s.id,
    templateId: s.templateId,
    status: s.status,
    requirementType: s.requirementType,
    parentLayoutElement: s.parentLayoutElement,
    lastCompletedAt:
      s.status === SubTaskStatus.DONE
        ? s.completedAt || findSubTaskCompletionEvent(s, events)?.at || null
        : null
  };
};

const denormalizeSubTaskCreateMeeting = (
  s: ISubTask,
  sTpl: ISubtaskCreateMeetingTemplate,
  replacements: ExperienceReplacements,
  tz: string,
  interpolationOptions: InterpolationDefaultOptions,
  resources: {
    answersById: { [subTaskId: string]: Doc<SubTaskQuestionAnswer> };
    calendlyEventsById: { [subTaskId: string]: Doc<CalendlyEventDetails> };
  },
  startsAt: Timestamp,
  instanceAnchorDate: Timestamp,
  events: IExperienceEvent[],
  allActors: { [actorKey: string]: Doc<IUser> }
): ISubtaskCreateMeetingDenormalized => {
  const participant = allActors[PARTICIPANT_ACTOR_KEY];
  const eventDate = getMeetingDate(
    sTpl,
    tz,
    startsAt,
    instanceAnchorDate,
    participant
  );
  const interpolate = (x: string) =>
    interpolateExperienceText(x, replacements, interpolationOptions);
  const resolvedInvitees: ISubtaskCreateMeetingDenormalized['data']['resolvedInvitees'] = compact(
    sTpl.data.pplToAdd.map((p) => {
      if (p.type === 'other') {
        return {
          fullName: '',
          email: interpolate(p.email)
        };
      }
      const user = allActors[p.key];
      if (!user) {
        // Hmmm what should we do here?
        console.log(`No user found for actor ${p.key}`);
        return null;
      }
      const email: string = get(user.data, p.emailField);
      if (!email) {
        // Hmmm another error case - how to handle?
        console.log(
          `No email address found for actor ${p.key} - ${p.emailField}`
        );
        return null;
      }
      return {
        fullName: toFullName(user.data),
        email
      };
    })
  );
  const title = interpolate(sTpl.data.title);
  const description = interpolate(sTpl.data.description);
  return {
    ...getCommonSubTasDenormalizedProps(s, events),
    type: sTpl.type,
    data: {
      title,
      description,
      eventDate: eventDate.toDate().toISOString(),
      resolvedInvitees,
      calendarLinks: sTpl.data.createLinksFor.map((service) => ({
        type: service,
        url: createCalendarEventCreationUrl(service, {
          title,
          description,
          start: eventDate,
          duration: sTpl.data.at.duration,
          repeat: sTpl.data.repeat,
          invitees: resolvedInvitees
        })
      })),
      repeat: sTpl.data.repeat,
      pplToAdd: sTpl.data.pplToAdd,
      at: sTpl.data.at,
      anchor: sTpl.data.anchor
    }
  };
};

/*
@guide Create a new subtask - 3.1 Denormalize the subtask

Create the denormalized version of your new subtask
This should also show an error- another assertNever switch statement

Mind that this step needs to be done here in the frontend, but also
within the Cloud Functions! Full-text search for toDenormalizedExperience
and implement the same code there.
*/
export const toDenormalizedSubTask = (
  s: ISubTask,
  sTpl: SubTaskTemplate,
  replacements: ExperienceReplacements,
  resources: {
    answersById: { [subTaskId: string]: Doc<SubTaskQuestionAnswer> };
    calendlyEventsById: { [subTaskId: string]: Doc<CalendlyEventDetails> };
  },
  interpolationOptions: InterpolationDefaultOptions,
  tz: string,
  startsAt: Timestamp,
  instanceAnchorDate: Timestamp,
  events: IExperienceEvent[],
  allActors: { [actorKey: string]: Doc<IUser> }
): SubTaskDenormalized => {
  const commonProps = getCommonSubTasDenormalizedProps(s, events);
  switch (sTpl.type) {
    case SubTaskType.CONFIRM:
      return {
        ...getCommonSubTasDenormalizedProps(s, events),
        type: sTpl.type,
        data: {
          ...sTpl.data,
          label: interpolateExperienceText(
            sTpl.data.label,
            replacements,
            interpolationOptions
          )
        }
      };
    case SubTaskType.MEET:
      return {
        ...commonProps,
        type: sTpl.type,
        data: sTpl.data
      };
    case SubTaskType.CREATE_MEETING:
      return denormalizeSubTaskCreateMeeting(
        s,
        sTpl,
        replacements,
        tz,
        interpolationOptions,
        resources,
        startsAt,
        instanceAnchorDate,
        events,
        allActors
      );
    case SubTaskType.SUBMIT:
      return {
        ...commonProps,
        type: sTpl.type,
        data: {
          ...sTpl.data,
          label: interpolateExperienceText(
            sTpl.data.label,
            replacements,
            interpolationOptions
          )
        }
      };
    case SubTaskType.WATCH:
      return {
        ...commonProps,
        type: sTpl.type,
        data: {
          ...sTpl.data,
          label: interpolateExperienceText(
            sTpl.data.label,
            replacements,
            interpolationOptions
          )
        }
      };
    case SubTaskType.VISIT:
      return {
        ...commonProps,
        type: sTpl.type,
        data: {
          ...sTpl.data,
          label: interpolateExperienceText(
            sTpl.data.label,
            replacements,
            interpolationOptions
          ),
          url: interpolateExperienceText(
            sTpl.data.url,
            replacements,
            interpolationOptions
          )
        }
      };
    case SubTaskType.QUESTION:
      return {
        ...commonProps,
        type: sTpl.type,
        data: {
          question: {
            ...sTpl.data.question,
            label: interpolateExperienceText(
              sTpl.data.question.label,
              replacements,
              interpolationOptions
            )
          },
          answer: getAnswer(sTpl, s.id, resources.answersById)
        }
      };
    case SubTaskType.CALENDLY_INVITE:
      const eventDetails = resources.calendlyEventsById[s.id] || null;
      const booking = eventDetails
        ? {
            startTime: eventDetails.data.startTime,
            endTime: eventDetails.data.endTime
          }
        : null;
      return {
        ...commonProps,
        type: sTpl.type,
        data: {
          booking,
          ...sTpl.data,
          label: interpolateExperienceText(
            sTpl.data.label,
            replacements,
            interpolationOptions
          )
        }
      };
    case SubTaskType.DOCUMENT_READ:
      return {
        ...commonProps,
        type: sTpl.type,
        data: {
          title: interpolateExperienceText(
            sTpl.data.title,
            replacements,
            interpolationOptions
          ),
          docUrl: sTpl.data.docUrl,
          thumbnailUrl: sTpl.data.thumbnailUrl,
          contentType: sTpl.data.contentType,
          pages: sTpl.data.pages,
          sizeInBytes: sTpl.data.sizeInBytes
        },
        completeOnDownload: sTpl.completeOnDownload
      };
    case SubTaskType.SWAG_UP_CHOICE:
      return {
        ...commonProps,
        type: sTpl.type,
        data: {
          question: sTpl.data,
          // TODO SWAGUP
          answer: null
        }
      };
    case SubTaskType.DATA_FIELD_CHANGE:
      return {
        ...commonProps,
        id: s.id,
        templateId: s.templateId,
        status: s.status,
        type: sTpl.type,
        data: {
          ...sTpl.data,
          instructions: {
            ...sTpl.data.instructions,
            t: interpolateExperienceText(
              sTpl.data.instructions.t,
              replacements,
              interpolationOptions
            )
          },
          rules: sTpl.data.rules
        }
      };
    case SubTaskType.DOCUSIGN_ENVELOPE_SIGN:
      return {
        ...commonProps,
        type: sTpl.type,
        data: {
          description: {
            ...sTpl.data.description,
            t: interpolateExperienceText(
              sTpl.data.description.t,
              replacements,
              interpolationOptions
            )
          },
          envelope: {
            name: '', // TODO DocuSign
            emailSubject: '',
            role: ''
          }
        }
      };
    case SubTaskType.BACKGROUND_CHECK_INVITE:
      return {
        ...commonProps,
        type: sTpl.type,
        data: {
          description: {
            ...sTpl.data.description,
            t: interpolateExperienceText(
              sTpl.data.description.t,
              replacements,
              interpolationOptions
            )
          },
          linkText: interpolateExperienceText(
            sTpl.data.linkText,
            replacements,
            interpolationOptions
          ),
          package: null,
          invitation: null
        }
      };
    case SubTaskType.COMPLETE_LMS_MODULE:
      return {
        ...commonProps,
        type: sTpl.type,
        data: {
          description: {
            ...sTpl.data.description,
            t: interpolateExperienceText(
              sTpl.data.description.t,
              replacements,
              interpolationOptions
            )
          },
          unit: sTpl.data.unit,
          supportContact: sTpl.data.supportContact,
          // This local/preview denormalizer has no completion data (that is
          // stitched in server-side from doceboCompletionsV1).
          completion: null
        }
      };
    default:
      return assertNever(sTpl);
  }
};

export const toDenormalizedTask = (
  t: ITask,
  taskTpl: ITaskTemplate,
  allActors: { [actorKey: string]: Doc<IUser> },
  startsAt: Timestamp,
  replacements: ExperienceReplacements,
  resources: {
    answersById: { [subTaskId: string]: Doc<SubTaskQuestionAnswer> };
    calendlyEventsById: { [subTaskId: string]: Doc<CalendlyEventDetails> };
  },
  events: IExperienceEvent[],
  tz: string,
  interpolationOptions: InterpolationDefaultOptions,
  actors: ExperienceActor[],
  experienceAnchorDate: Timestamp,
  experienceEvents: IExperienceEvent[],
  opts: Partial<{
    ignoreAudience: boolean;
  }> = {}
): ITaskDenormalized => {
  const subTaskTplsById = keyBy(getSubTaskTemplates(taskTpl), (s) => s.id);
  const subTasks: SubTaskDenormalized[] = compact(
    t.subTasks.map<SubTaskDenormalized | null>((s) => {
      const sTpl = subTaskTplsById[s.templateId];
      if (!sTpl) {
        console.warn('SubTask template not found', {
          t,
          taskTpl,
          templateId: t.templateId,
          taskTemplateId: t.templateId,
          subTaskTemplateId: s.templateId
        });
        return null;
      }
      return toDenormalizedSubTask(
        s,
        sTpl,
        replacements,
        resources,
        interpolationOptions,
        tz,
        startsAt,
        experienceAnchorDate,
        experienceEvents,
        allActors
      );
    })
  );

  const subTasksByTplId = keyBy(t.subTasks, (s) => s.templateId);
  const blocks: TaskBlockDenormalized[] = compact(
    (opts.ignoreAudience
      ? taskTpl.blocks
      : filterBlocksForAudience(
          taskTpl.blocks,
          getAudienceById(taskTpl.audiences, t.audience)
        )
    ).map((b) => {
      if (isSubTaskTemplateBlock(b)) {
        const s = subTasksByTplId[b.id];
        if (!s) {
          console.warn('SubTask not found', {
            block: b,
            taskTpl
          });
          return null;
        }
        return toDenormalizedSubTask(
          s,
          b,
          replacements,
          resources,
          interpolationOptions,
          tz,
          startsAt,
          experienceAnchorDate,
          experienceEvents,
          allActors
        );
      }
      if (isContentTemplateBlock(b)) {
        return toDenormalizedContentBlock(
          b,
          replacements,
          interpolationOptions
        );
      }

      if (isLayoutTemplateBlock(b)) {
        return toDenormalizedLayoutBlock(b);
      }

      return assertNever(b);
    })
  );

  const { start, end } = t.timing;
  const d: ITaskDenormalized = {
    id: t.id,
    experienceId: t.experienceId,
    templateId: t.templateId,
    batchMode: taskTpl.batchMode,
    actor: t.actor,
    status: t.status,
    adminName: taskTpl.adminName
      ? interpolateExperienceText(taskTpl.adminName, replacements, interpolationOptions)
      : taskTpl.adminName,
    title: interpolateExperienceText(
      taskTpl.title,
      replacements,
      interpolationOptions
    ),
    description: {
      ...taskTpl.description,
      t: interpolateExperienceText(
        taskTpl.description.t,
        replacements,
        interpolationOptions
      )
    },
    start: t.timing.start,
    end: t.timing.end,
    reminders: compact(
      taskTpl.reminders.map((r) => {
        let beforeEnd: number | null = null;
        if (r.beforeEnd !== null) {
          beforeEnd = r.beforeEnd;
        }
        if (r.afterStart !== null) {
          beforeEnd = Math.round(
            end.toMillis() -
              start.toMillis() +
              (r.afterStart * ONE_DAY) / ONE_DAY
          );
        }

        if (beforeEnd === null) {
          return null;
        }

        return {
          id: r.id,
          beforeEnd
        };
      })
    ),
    remindUntilDone: taskTpl.remindUntilDone,
    subTasks,
    blocks,
    customCompletionCriteria: [],
    completedAt: t.completedAt || null,
    customSender: interpolateCustomSender(
      taskTpl.customSender,
      replacements,
      interpolationOptions
    )
  };

  return d;
};

export const toDenormalizedTasks = (
  exp: Doc<IExperience>,
  tpl: Doc<IExperienceTemplate>,
  allActors: { [actorKey: string]: Doc<IUser> },
  actorKey: string | '_____EVERYONE______',
  replacements: ExperienceReplacements,
  resources: {
    answers?: Doc<SubTaskQuestionAnswer>[];
    calendlyEventDetails?: Doc<CalendlyEventDetails>[];
  },
  interpolationOptions: InterpolationDefaultOptions,
  otherOptions: { includeSkippedITems: boolean; ignoreAudience?: boolean }
) => {
  const logger = console;
  const startsAt = exp.data.startsAt;
  const taskTemplatesById = keyBy(tpl.data.tasks, (t) => t.id);
  const answersById = keyBy(resources.answers || [], (a) => a.id);

  const calendlyEventsById = keyBy(
    resources.calendlyEventDetails || [],
    (a) => a.id
  );
  const tasks: ITaskDenormalized[] = compact(
    exp.data.tasks.map((t) => {
      if (actorKey !== '_____EVERYONE______' && t.actor !== actorKey) {
        return null;
      }
      if (
        !otherOptions.includeSkippedITems &&
        t.status === TaskStatus.SKIPPED
      ) {
        return null;
      }
      const taskTpl = taskTemplatesById[t.templateId];
      if (!taskTpl) {
        logger.warn('Task template not found', {
          exp,
          tpl,
          taskTemplateId: t.templateId
        });
        return null;
      }
      return toDenormalizedTask(
        t,
        taskTpl,
        allActors,
        startsAt,
        replacements,
        {
          answersById,
          calendlyEventsById
        },
        exp.data.events,
        tpl.data.tz,
        interpolationOptions,
        exp.data.actors,
        exp.data.anchorDate.at,
        exp.data.events,
        { ignoreAudience: otherOptions.ignoreAudience }
        //pass the experience here?
      );
    })
  );
  return tasks;
};

/*
@guide Create a new automation action - 3.1 Denormalize the automation action

Create the denormalized version of your new automation action.

Mind that this step needs to be done here in the frontend, but also
within the Cloud Functions! Full-text search for toDenormalizedExperience
and implement the same code there.
*/
export const toDenormalizedAutomationAction = (
  acTpl: AutomationActionTemplate,
  ac: AutomationAction
): AutomationActionDenormalized | null => {
  if (ac.type !== acTpl.type) {
    console.warn('TYPE_MISMATCH', ac, acTpl);
    return null;
  }
  if (
    ac.type === AutomationActionType.OKTA_ADD_TO_GROUP &&
    acTpl.type === AutomationActionType.OKTA_ADD_TO_GROUP
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      group: acTpl.group,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd
    };
  }
  if (
    ac.type === AutomationActionType.ENROLL_IN_EXPERIENCE &&
    acTpl.type === AutomationActionType.ENROLL_IN_EXPERIENCE
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      status: ac.status,
      actorToEnroll: acTpl.actorToEnroll,
      tplToEnrollIn: acTpl.tplToEnrollIn,
      supportingActors: acTpl.supportingActors,
      timing: acTpl.timing,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt
    };
  }
  if (
    ac.type === AutomationActionType.GOOGLE_ADD_TO_GROUP &&
    acTpl.type === AutomationActionType.GOOGLE_ADD_TO_GROUP
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      group: acTpl.group,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd
    };
  }
  if (
    ac.type === AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION &&
    acTpl.type === AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      calendar: acTpl.calendar,
      query: acTpl.query,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      anchor: acTpl.anchor,
      pplToAdd: acTpl.pplToAdd
    };
  }
  if (
    ac.type === AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION &&
    acTpl.type === AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      calendar: acTpl.calendar,
      query: acTpl.query,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd,
      anchor: acTpl.anchor
    };
  }
  if (
    ac.type === AutomationActionType.ASANA_CREATE_TASK &&
    acTpl.type === AutomationActionType.ASANA_CREATE_TASK
  ) {
    return {
      id: ac.id,
      title: acTpl.title,
      templateId: acTpl.id,
      type: ac.type,
      workspace: acTpl.workspace,
      project: acTpl.project,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt
    };
  }
  if (
    ac.type === AutomationActionType.ATLASSIAN_CREATE_ISSUE &&
    acTpl.type === AutomationActionType.ATLASSIAN_CREATE_ISSUE
  ) {
    // TODO: ask why not all fields are deserialized
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      project: acTpl.project,
      issueType: acTpl.issueType,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      summary: acTpl.summary
    };
  }
  if (
    ac.type === AutomationActionType.SLACK_INVITE_TO_CHANNEL &&
    acTpl.type === AutomationActionType.SLACK_INVITE_TO_CHANNEL
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      channel: acTpl.channel,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd
    };
  }
  if (
    ac.type === AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP &&
    acTpl.type === AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      group: acTpl.group,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd
    };
  }
  if (
    ac.type === AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL &&
    acTpl.type === AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd,
      channel: acTpl.channel
    };
  }
  if (
    ac.type === AutomationActionType.ATLASSIAN_CREATE_ISSUE &&
    acTpl.type === AutomationActionType.ATLASSIAN_CREATE_ISSUE
  ) {
    // TODO: ask why not all fields are deserialized
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      project: acTpl.project,
      issueType: acTpl.issueType,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      summary: acTpl.summary
    };
  }

  if (
    ac.type === AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT &&
    acTpl.type === AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT
  ) {
    // TODO: ask why not all fields are deserialized
    return {
      id: ac.id,
      templateId: ac.templateId,
      doneAt: ac.doneAt,
      status: ac.status,
      type: AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT,
      content: acTpl.content,
      pplToAdd: acTpl.pplToAdd,
      duration: acTpl.duration
    };
  }

  if (
    ac.type === AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE &&
    acTpl.type === AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE
  ) {
    return {
      id: ac.id,
      type: AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE,
      target: { ...acTpl.target },
      errorData: ac.errorData,
      srcTemplate: { ...acTpl.srcTemplate },
      fileNameFormat: acTpl.fileNameFormat,
      status: ac.status,
      doneAt: ac.doneAt,
      templateId: ac.templateId
    };
  }

  if (
    ac.type === AutomationActionType.HTTP_POST &&
    acTpl.type === AutomationActionType.HTTP_POST
  ) {
    return {
      id: ac.id,
      type: AutomationActionType.HTTP_POST,
      status: ac.status,
      doneAt: ac.doneAt,
      requestUrl: acTpl.requestUrl,
      auth: acTpl.auth,
      jsonPayload: acTpl.jsonPayload,
      templateId: ac.templateId,
      errorData: ac.errorData
    };
  }
  if (
    ac.type === AutomationActionType.UNENROLL_FROM_EXPERIENCE &&
    acTpl.type === AutomationActionType.UNENROLL_FROM_EXPERIENCE
  ) {
    return {
      id: ac.id,
      type: AutomationActionType.UNENROLL_FROM_EXPERIENCE,
      status: ac.status,
      doneAt: ac.doneAt,
      templateId: ac.templateId,
      data: null,
      errorData: ac.errorData
    };
  }

  if (
    ac.type === AutomationActionType.DOCEBO_ENROLL &&
    acTpl.type === AutomationActionType.DOCEBO_ENROLL
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      target: acTpl.target,
      status: ac.status,
      pplToAdd: acTpl.pplToAdd,
      doneAt: ac.doneAt,
      data: ac.data,
      errorData: ac.errorData
    };
  }

  if (
    ac.type === AutomationActionType.SYNC_TO_DATA_DESTINATION &&
    acTpl.type === AutomationActionType.SYNC_TO_DATA_DESTINATION
  ) {
    return {
      id: ac.id,
      templateId: ac.templateId,
      type: AutomationActionType.SYNC_TO_DATA_DESTINATION,
      // Synthetic display status: 'RUNNING' while the async sync is
      // in flight (still UPCOMING at runtime), otherwise the plain status.
      status: denormalizedActionDisplayStatus(ac),
      doneAt: ac.doneAt,
      destination: acTpl.destination,
      mappings: acTpl.mappings,
      errorData: ac.errorData
    };
  }
  console.warn('UNKNOWN_ACTION_TYPE', { acTpl, ac });
  return null;
};

export const toDenormalizedAutomationActionWithInterpolations = (
  acTpl: AutomationActionTemplate,
  ac: AutomationAction,
  replacements: ExperienceReplacements,
  resources: {
    answers?: Doc<SubTaskQuestionAnswer>[];
    calendlyEventDetails?: Doc<CalendlyEventDetails>[];
  },
  interpolationOptions: InterpolationDefaultOptions
): AutomationActionDenormalized | null => {
  if (ac.type !== acTpl.type) {
    console.warn('TYPE_MISMATCH', ac, acTpl);
    return null;
  }
  if (
    ac.type === AutomationActionType.OKTA_ADD_TO_GROUP &&
    acTpl.type === AutomationActionType.OKTA_ADD_TO_GROUP
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      group: acTpl.group,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd
    };
  }
  if (
    ac.type === AutomationActionType.ENROLL_IN_EXPERIENCE &&
    acTpl.type === AutomationActionType.ENROLL_IN_EXPERIENCE
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      status: ac.status,
      actorToEnroll: acTpl.actorToEnroll,
      tplToEnrollIn: acTpl.tplToEnrollIn,
      supportingActors: acTpl.supportingActors,
      timing: acTpl.timing,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt
    };
  }
  if (
    ac.type === AutomationActionType.GOOGLE_ADD_TO_GROUP &&
    acTpl.type === AutomationActionType.GOOGLE_ADD_TO_GROUP
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      group: acTpl.group,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd
    };
  }
  if (
    ac.type === AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION &&
    acTpl.type === AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      calendar: acTpl.calendar,
      query: acTpl.query,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      anchor: acTpl.anchor,
      pplToAdd: acTpl.pplToAdd
    };
  }
  if (
    ac.type === AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION &&
    acTpl.type === AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      calendar: acTpl.calendar,
      query: acTpl.query,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd,
      anchor: acTpl.anchor
    };
  }
  if (
    ac.type === AutomationActionType.ASANA_CREATE_TASK &&
    acTpl.type === AutomationActionType.ASANA_CREATE_TASK
  ) {
    return {
      id: ac.id,
      title: acTpl.title,
      templateId: acTpl.id,
      type: ac.type,
      workspace: acTpl.workspace,
      project: acTpl.project,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt
    };
  }
  if (
    ac.type === AutomationActionType.ATLASSIAN_CREATE_ISSUE &&
    acTpl.type === AutomationActionType.ATLASSIAN_CREATE_ISSUE
  ) {
    // TODO: ask why not all fields are deserialized
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      project: acTpl.project,
      issueType: acTpl.issueType,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      summary: acTpl.summary
    };
  }
  if (
    ac.type === AutomationActionType.SLACK_INVITE_TO_CHANNEL &&
    acTpl.type === AutomationActionType.SLACK_INVITE_TO_CHANNEL
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      channel: acTpl.channel,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd
    };
  }
  if (
    ac.type === AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP &&
    acTpl.type === AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      group: acTpl.group,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd
    };
  }
  if (
    ac.type === AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL &&
    acTpl.type === AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      pplToAdd: acTpl.pplToAdd,
      channel: acTpl.channel
    };
  }
  if (
    ac.type === AutomationActionType.ATLASSIAN_CREATE_ISSUE &&
    acTpl.type === AutomationActionType.ATLASSIAN_CREATE_ISSUE
  ) {
    // TODO: ask why not all fields are deserialized
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      project: acTpl.project,
      issueType: acTpl.issueType,
      status: ac.status,
      data: ac.data,
      errorData: ac.errorData,
      doneAt: ac.doneAt,
      summary: acTpl.summary
    };
  }

  if (
    ac.type === AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT &&
    acTpl.type === AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT
  ) {
    // TODO: ask why not all fields are deserialized
    return {
      id: ac.id,
      templateId: ac.templateId,
      doneAt: ac.doneAt,
      status: ac.status,
      type: AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT,
      content: acTpl.content,
      pplToAdd: acTpl.pplToAdd,
      duration: acTpl.duration
    };
  }

  if (
    ac.type === AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE &&
    acTpl.type === AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE
  ) {
    return {
      id: ac.id,
      type: AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE,
      target: { ...acTpl.target },
      errorData: ac.errorData,
      srcTemplate: { ...acTpl.srcTemplate },
      fileNameFormat: interpolateExperienceText(
        acTpl.fileNameFormat,
        replacements,
        interpolationOptions
      ),
      createdDocPublicUrl: ac.data?.createdDocPublicUrl,
      status: ac.status,
      doneAt: ac.doneAt,
      templateId: ac.templateId
    };
  }

  if (
    ac.type === AutomationActionType.HTTP_POST &&
    acTpl.type === AutomationActionType.HTTP_POST
  ) {
    return {
      id: ac.id,
      type: AutomationActionType.HTTP_POST,
      status: ac.status,
      doneAt: ac.doneAt,
      requestUrl: interpolateExperienceText(
        acTpl.requestUrl,
        replacements,
        interpolationOptions
      ),
      auth: acTpl.auth,
      jsonPayload: interpolateExperienceText(
        acTpl.jsonPayload,
        replacements,
        interpolationOptions
      ),
      templateId: ac.templateId,
      errorData: ac.errorData
    };
  }

  if (
    ac.type === AutomationActionType.DOCEBO_ENROLL &&
    acTpl.type === AutomationActionType.DOCEBO_ENROLL
  ) {
    return {
      id: ac.id,
      templateId: acTpl.id,
      type: ac.type,
      target: acTpl.target,
      status: ac.status,
      pplToAdd: acTpl.pplToAdd,
      doneAt: ac.doneAt,
      data: ac.data,
      errorData: ac.errorData
    };
  }

  if (
    ac.type === AutomationActionType.SYNC_TO_DATA_DESTINATION &&
    acTpl.type === AutomationActionType.SYNC_TO_DATA_DESTINATION
  ) {
    return {
      id: ac.id,
      templateId: ac.templateId,
      type: AutomationActionType.SYNC_TO_DATA_DESTINATION,
      // Synthetic display status: 'RUNNING' while the async sync is
      // in flight (still UPCOMING at runtime), otherwise the plain status.
      status: denormalizedActionDisplayStatus(ac),
      doneAt: ac.doneAt,
      destination: acTpl.destination,
      mappings: acTpl.mappings,
      errorData: ac.errorData
    };
  }
  console.warn('UNKNOWN_ACTION_TYPE', { acTpl, ac });
  return null;
};

const toDenormalizedAutomation = (
  a: IAutomation,
  tpl: IAutomationTemplate,
  startsAt: Timestamp,
  tz: string
): IAutomationDenormalized | null => {
  const start = a.timing.at;
  const actionTplsById = keyBy(tpl.actions, (ac) => ac.id);
  return {
    id: a.id,
    templateId: tpl.id,
    title: tpl.title,
    description: tpl.description,
    // Synthetic rollup: 'RUNNING' while an in-flight sync action is polling
    // (automation still UPCOMING at runtime), otherwise the plain status.
    // Mirrors the per-action denormalizedActionDisplayStatus.
    status: denormalizedAutomationDisplayStatus(a),
    completedAt: a.completedAt,
    timing: {
      type: 'ABSOLUTE',
      start
    },
    actions: compact(
      Object.entries(a.actions).map<AutomationActionDenormalized | null>(
        ([acTplId, ac]) => {
          const acTpl = actionTplsById[acTplId];
          if (!acTpl) {
            console.warn('ACTION_TEMPLATE_NOT_FOUND', a, tpl, acTplId);
            return null;
          }
          if (ac.type !== acTpl.type) {
            console.warn('TYPE_MISMATCH', ac, acTpl);
            return null;
          }
          return toDenormalizedAutomationAction(acTpl, ac);
        }
      )
    )
  };
};

const toDenormalizedAutomationWithInterpolations = (
  a: IAutomation,
  tpl: IAutomationTemplate,
  replacements: ExperienceReplacements,
  resources: {
    answers?: Doc<SubTaskQuestionAnswer>[];
    calendlyEventDetails?: Doc<CalendlyEventDetails>[];
  },
  interpolationOptions: InterpolationDefaultOptions
): IAutomationDenormalized | null => {
  const start = a.timing.at;
  const actionTplsById = keyBy(tpl.actions, (ac) => ac.id);
  return {
    id: a.id,
    templateId: tpl.id,
    title: tpl.title,
    description: tpl.description,
    // Synthetic rollup: 'RUNNING' while an in-flight sync action is polling
    // (automation still UPCOMING at runtime), otherwise the plain status.
    // Mirrors the per-action denormalizedActionDisplayStatus.
    status: denormalizedAutomationDisplayStatus(a),
    completedAt: a.completedAt,
    timing: {
      type: 'ABSOLUTE',
      start
    },
    actions: compact(
      Object.entries(a.actions).map<AutomationActionDenormalized | null>(
        ([acTplId, ac]) => {
          const acTpl = actionTplsById[acTplId];
          if (!acTpl) {
            console.warn('ACTION_TEMPLATE_NOT_FOUND', a, tpl, acTplId);
            return null;
          }
          if (ac.type !== acTpl.type) {
            console.warn('TYPE_MISMATCH', ac, acTpl);
            return null;
          }
          return toDenormalizedAutomationActionWithInterpolations(
            acTpl,
            ac,
            replacements,
            resources,
            interpolationOptions
          );
        }
      )
    )
  };
};

export const toDenormalizedAutomations = (
  exp: Doc<IExperience>,
  tpl: Doc<IExperienceTemplate>
) => {
  const aTplsById = keyBy(tpl.data.automations, (x) => x.id);
  return compact(
    Object.entries(exp.data.automations).map(([tplId, a]) => {
      const aTpl = aTplsById[tplId];
      if (!aTpl) {
        console.warn('ACTION_TEMPLATE_NOT_FOUND', {
          exp,
          tpl,
          tplId,
          action: a
        });
        return null;
      }

      return toDenormalizedAutomation(a, aTpl, exp.data.startsAt, tpl.data.tz);
    })
  );
};

export const toDenormalizedAutomationsWithInterpolations = (
  exp: Doc<IExperience>,
  tpl: Doc<IExperienceTemplate>,
  replacements: ExperienceReplacements,
  resources: {
    answers?: Doc<SubTaskQuestionAnswer>[];
    calendlyEventDetails?: Doc<CalendlyEventDetails>[];
  },
  interpolationOptions: InterpolationDefaultOptions
) => {
  const aTplsById = keyBy(tpl.data.automations, (x) => x.id);
  return compact(
    Object.entries(exp.data.automations).map(([tplId, a]) => {
      const aTpl = aTplsById[tplId];
      if (!aTpl) {
        console.warn('ACTION_TEMPLATE_NOT_FOUND', {
          exp,
          tpl,
          tplId,
          action: a
        });
        return null;
      }

      return toDenormalizedAutomationWithInterpolations(
        a,
        aTpl,
        replacements,
        resources,
        interpolationOptions
      );
    })
  );
};

const answerStore = createDocumentListenerGetter(
  (id) => store().collection(FS.subTaskQuestionAnswer).doc(id),
  toSubTaskQuestionAnswerDoc
);

const useAnswers = (answerIds: string[]) => {
  const listeners = useMemo(() => answerIds.map(answerStore), [answerIds]);
  return useDocumentListeners(listeners);
};

export const useAdditionalDenormalizationResources = (
  doc: Doc<IExperience>
): LoadingValue<DenormalizationResources> => {
  const answerIds = useMemo(() => {
    const res: string[] = [];
    doc.data.tasks.forEach((t) => {
      t.subTasks.forEach((s) => {
        if (s.type === 'QUESTION') {
          res.push(s.id);
        }
      });
    });
    return res;
  }, [doc]);

  return useMappedLoadingValue(useAnswers(answerIds), (answers) => {
    return { answers: compact(answers) };
  });
};

export type DenormalizationResources = {
  answers?: Doc<SubTaskQuestionAnswer>[];
};

const actorToInterpolationVariable = (
  key: string,
  label: string,
  user: Doc<IUser>,
  customFields: CustomUserField[],
  referenceDate: Timestamp,
  dateOverrides: { [overridePath: string]: Timestamp } = {}
): InterpolationVariable => {
  return {
    key,
    label,
    vars: userToInterpolation(
      user.data,
      customFields,
      referenceDate,
      dateOverrides
    )
  };
};

const getAnswerAsText = (answer: SubTaskQuestionAnswer): string => {
  const { d } = answer;
  switch (d.type) {
    case QuestionType.TEXT:
      return d.value;
    case QuestionType.SELECT:
      return d.value.label;
    case QuestionType.MULTI_SELECT:
      return d.value.map((v) => v.label).join(', ');
    case QuestionType.ADDRESS:
      return addressToText(d.value);
    case QuestionType.SUPPORTING_ACTOR:
      return d.value;
    case QuestionType.LINEAR_SCALE:
    case QuestionType.RATING:
      return formatScaleAnswer(d);
    case SubTaskType.SWAG_UP_CHOICE:
      // Should never end up here
      return '';
  }
};

const interpolateCustomSender = (
  customSender:
    | {
        name: string;
        replyTo: string;
      }
    | null
    | undefined,
  replacements: ExperienceReplacements,
  opts: InterpolationDefaultOptions
) => {
  if (!customSender) {
    return;
  }
  let name = customSender.name
    ? interpolateExperienceText(customSender.name, replacements, opts)
    : '';
  if (name !== '' && (isReplacementToken(name) || name.includes('Unknown'))) {
    console.log('Unable to replace name', customSender);
    name = '';
  }
  let replyTo = customSender.replyTo
    ? interpolateExperienceText(customSender.replyTo, replacements, opts)
    : '';
  if (replyTo !== '' && !isValidEmail(replyTo)) {
    console.log(`No valid email found for replyTo`, customSender);
    replyTo = '';
  }
  return { name, replyTo };
};

export const collectReplacements = (
  // we'll see what needs to be passed here later when we have all roles etc
  tpl: Doc<IExperienceTemplate>,
  exp: Doc<IExperience>,
  orgName: string,
  actors: { [actorKey: string]: Doc<IUser> },
  customFields: CustomUserField[],
  referenceDate: Timestamp,
  additionalResources?: {
    answers?: Doc<SubTaskQuestionAnswer>[];
  }
): ExperienceReplacements => {
  return {
    experience: {
      vars: {
        title: tpl.data.title,
        tasks: fromPairs(
          tpl.data.tasks.map((task) => {
            const instance = exp.data.tasks.find(
              (t) => t.templateId === task.id
            );
            return [
              task.id,
              {
                url: '', // TODO - not totally trivial to figure out, as this needs to look at the actor, find the access token, find the instance ids etc...
                subTasks: fromPairs(
                  compact(
                    task.blocks.map((b) => {
                      if (!isSubTaskTemplateBlock(b)) {
                        return null;
                      }
                      const subTask = instance?.subTasks.find(
                        (s) => s.templateId === b.id
                      );
                      const completed: 'Yes' | 'No' =
                        subTask?.status === SubTaskStatus.DONE ? 'Yes' : 'No';
                      if (b.type === SubTaskType.QUESTION) {
                        const answer = additionalResources?.answers?.find(
                          (a) => a.data.sTplId === b.id
                        );
                        return [
                          b.id,
                          {
                            type: SubTaskType.QUESTION,
                            completed,
                            answer: answer
                              ? getAnswerAsText(answer.data)
                              : 'No answer given yet' // TODO translate
                          }
                        ];
                      }
                      if (b.type === SubTaskType.SUBMIT) {
                        // Frontend does not have access to submittedFiles today,
                        // so url cannot be resolved in previews. Backend send-time
                        // interpolation is the source of truth for SUBMIT urls.
                        return [
                          b.id,
                          {
                            type: SubTaskType.SUBMIT,
                            completed,
                            url: ''
                          }
                        ];
                      }
                      return [
                        b.id,
                        {
                          type: b.type,
                          completed
                        }
                      ];
                    })
                  )
                )
              }
            ];
          })
        )
      }
    },
    org: { vars: { name: orgName } },
    roles: [
      {
        key: PARTICIPANT_ACTOR_KEY,
        label: 'Participant'
      },
      ...Object.values(tpl.data.supportingActors).map((a) => ({
        key: a.key,
        label: a.name
      }))
    ].map(({ key, label }) =>
      actorToInterpolationVariable(
        key,
        label,
        actors[key] || UNKNOWN_USER(),
        customFields,
        referenceDate,
        key === PARTICIPANT_ACTOR_KEY &&
          tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE
          ? {
              [tpl.data.anchor.field]: exp.data.anchorDate.at
            }
          : undefined
      )
    )
  };
};

export const toDenormalizedMessage = (
  t: ExperienceTemplateMessage,
  audience: Audience,
  replacements: ExperienceReplacements,
  status: MessageStatus,
  at: Timestamp,
  interpolationOptions: InterpolationDefaultOptions,
  opts: Partial<{
    ignoreAudience: boolean;
    allowEmptyBlocks?: boolean;
  }> = {}
): IExperienceMessageDenormalized | null => {
  const blocks = (opts?.ignoreAudience
    ? t.blocks
    : filterBlocksForAudience(t.blocks, audience)
  ).map((b) =>
    isContentTemplateBlock(b)
      ? toDenormalizedContentBlock(b, replacements, interpolationOptions)
      : toDenormalizedLayoutBlock(b)
  );

  if (!blocks.length && !opts?.ignoreAudience && !opts.allowEmptyBlocks) {
    console.log('bbb', blocks, opts);
    console.log('No blocks for message - skipping', { t, audience });
    return null;
  }

  return {
    templateId: t.id,
    adminName: t.adminName
      ? interpolateExperienceText(t.adminName, replacements, interpolationOptions)
      : t.adminName,
    subject: interpolateExperienceText(
      t.subject,
      replacements,
      interpolationOptions
    ),
    body: {
      ...t.body,
      t: interpolateExperienceText(t.body.t, replacements, interpolationOptions)
    },
    blocks,
    sendAt: at,
    status: status,
    taskList: t.taskList,
    actor: t.actor,
    batchMode: t.batchMode,
    markedAsRead: null,
    customSender: interpolateCustomSender(
      t.customSender,
      replacements,
      interpolationOptions
    )
  };
};

export const toDenormalizedMessages = (
  exp: Doc<IExperience>,
  tpl: Doc<IExperienceTemplate>,
  actorKey: string | '_____EVERYONE______',
  participant: Doc<IUser>,
  replacements: ExperienceReplacements,
  interpolationOptions: InterpolationDefaultOptions,
  options: {
    ignoreCurrentTime: boolean;
    includeSkippedItems: boolean;
    ignoreAudience?: boolean;
  }
): IExperienceMessageDenormalized[] => {
  const n = Date.now(); // might wanna pass this in from the outside
  const allMsgTpls = compact([
    ...tpl.data.messages.other,
    tpl.data.messages.outro
  ]).filter((m) => m.actor === actorKey || actorKey === '_____EVERYONE______');
  const sortedTpls = sortBy(
    allMsgTpls,
    (m) =>
      getFixedTimingOrApproximateParentTiming(m.timing, tpl.data.tasks).start
  );
  const currentTpls = sortedTpls.filter((m) =>
    // Message might be taken out for this instance because of audiences or other reasons
    {
      const msgStatus = exp.data.messagesStatuses[m.id];
      return (
        !!msgStatus &&
        (options.includeSkippedItems ||
          msgStatus.status !== MessageStatus.SKIPPED) &&
        (options.ignoreCurrentTime || msgStatus.timing.at.toMillis() <= n)
      );
    }
  );
  // TODO Triggered Items - not clear why this is still there
  // We commented it out for now, which I think is the right thing to do
  //
  // always take the first one
  // if (!currentTpls.length && sortedTpls.length) {
  //   currentTpls.push(sortedTpls[0]);
  // }

  return compact(
    currentTpls.map((t) => {
      // GOLDEN RULE: use the audience FROZEN on the instance message status
      // (stamped at enrollment, preserved for started/sent items), NOT a
      // re-derive from the participant's CURRENT data. Re-deriving here dropped
      // already-sent messages from the Status + Log views once the participant's
      // data moved them out of the message's audience — the message has no
      // blocks for the new audience, so toDenormalizedMessage returned null.
      // (The Timeline reads the raw instance, so it kept them — matching the
      // reported bug.) Mirrors toDenormalizedTask, which already resolves via
      // getAudienceById(taskTpl.audiences, t.audience). Falls back to a
      // re-derive only when no audience was stored (legacy instances).
      const frozenAudienceId = exp.data.messagesStatuses[t.id]?.audience;
      const audience = frozenAudienceId
        ? getAudienceById(t.audiences, frozenAudienceId)
        : getAudienceForUser(t.audiences, participant, exp.data.anchorDate.at);
      return toDenormalizedMessage(
        t,
        audience,
        replacements,
        exp.data.messagesStatuses[t.id]?.status || MessageStatus.SKIPPED,
        exp.data.messagesStatuses[t.id]?.timing.at ||
          // We should never end up in this code path
          getFreshNonTaskStartDate(
            getFixedTimingOrApproximateParentTiming(t.timing, tpl.data.tasks),
            exp.data.anchorDate.at,
            tpl.data.anchor,
            tpl.data.tz
          ),
        interpolationOptions,
        options
      );
    })
  );
};

const denormalizeSsoInfo = (
  orgSso: IOrganization['sso'],
  tplSso: IExperienceTemplate['sso'],
  user: Doc<IUser>
): IExperienceDenormalized['sso'] => {
  if (!orgSso.enabledForEndUsers || !tplSso) {
    return { required: false };
  }
  if (tplSso.type === 'AFTER_FIRST_DAY_AT_WORK') {
    // If a user has no hireDate they are probably a supporting actor
    // with incomplete information. In such a case we assume the date must be
    // at some point in the past - requiring SSO
    if (user.data.hireDate) {
      if (!tsIsAfter(now(), addToTs(user.data.hireDate, tplSso.offset))) {
        return { required: false };
      }
    }
  }

  return { required: true, provider: orgSso.provider };
};

export const toDenormalizedExperience = (
  actorKey: string,
  exp: Doc<IExperience>,
  tpl: Doc<IExperienceTemplate>,
  org: Doc<IOrganization>,
  allActors: { [actorKey: string]: Doc<IUser> },
  resources: DenormalizationResources,

  // the current time is typically used to filter out messages that shouldn't have been sent at the current point in time
  // use the option to ignore this behavior to always include all messages
  options: {
    ignoreCurrentTime: boolean;
    includeSkippedItems: boolean;
    ignoreAudience?: boolean;
  }
): Doc<IExperienceDenormalized> => {
  const actor = getActorForKey(exp, actorKey);
  if (!actor) {
    throw new Error('ACTOR_NOT_FOUND');
  }
  const { startsAt } = exp.data;

  const denormalizedOrg = toDenormalizedOrg(
    org.data,
    tpl.data.overrides?.org || null
  );
  const replacements = collectReplacements(
    tpl,
    exp,
    denormalizedOrg.name,
    allActors,
    org.data.customUserFields,
    exp.data.anchorDate.at
  );

  const interpolationOptions: InterpolationDefaultOptions = {
    dateFormat: org.data.dateFormats.MMDDYYYY
  };

  const interpolateText = (txt: string) =>
    interpolateExperienceText(txt, replacements, interpolationOptions);

  const user: Doc<IUser> | undefined = allActors[actorKey] as any;
  const participant = allActors[PARTICIPANT_ACTOR_KEY];

  const tasks = sortBy(
    toDenormalizedTasks(
      exp,
      tpl,
      allActors,
      actorKey,
      replacements,
      resources,
      interpolationOptions,
      {
        includeSkippedITems: options.includeSkippedItems,
        ignoreAudience: options.ignoreAudience
      }
    ),
    (t) => t.start.toMillis()
  );

  const customSalutations = tpl.data.overrides?.org?.salutations || {};
  const customFallbackSalutations =
    tpl.data.overrides?.org?.fallbackSalutations || {};

  const salutation = getSalutation(
    actor.key,
    customSalutations,
    customFallbackSalutations,
    replacements,
    interpolationOptions
  );

  const data: IExperienceDenormalized = {
    accessToken: actor.accessToken,
    expId: exp.id,
    orgId: exp.data.orgId,
    org: denormalizedOrg,
    actor: actorKey,
    userId: isExperienceUserActor(actor) ? actor.userId : null,
    user: user
      ? toDenormalizedUser(user)
      : {
          publicId: 'slack',
          firstName: 'Slack',
          lastName: 'Channel',
          preferredName: 'Slack',
          email: 'slack@channel.com'
        },
    participantId: participant.id,
    participant: toDenormalizedUser(participant),
    templateId: exp.data.templateId,
    salutation,
    title: tpl.data.title,
    description: { t: '', v: 1 },
    status: exp.data.status,

    startsAt,
    tasks,
    contacts: tpl.data.contacts.map<IExperienceContact>((x) => {
      if (x.type === 'MAIL') {
        return {
          name: interpolateText(x.name || ''),
          email: interpolateText(x.email || ''),
          type: x.type
        };
      }
      if (x.type === 'SLACK') {
        return {
          name: interpolateText(x.name || ''),
          conversationId: interpolateText(x.conversationId || ''),
          type: x.type
        };
      }
      return x;
    }),
    notificationSender: {
      name: interpolateText(tpl.data.notificationSender.name || ''),
      replyTo: interpolateText(tpl.data.notificationSender.replyTo || '')
    },
    messages: toDenormalizedMessages(
      exp,
      tpl,
      actorKey,
      allActors[PARTICIPANT_ACTOR_KEY],
      replacements,
      interpolationOptions,
      options
    ),
    sso: user ? denormalizeSsoInfo(org.data.sso, tpl.data.sso, user) : null,
    customHints: {
      ...(org.data.customHints?.[actorKey] || {}),
      ...(tpl.data.overrides?.org?.customHints?.[actorKey] || {})
    },

    v: 1
  };

  return {
    id: actor.accessToken,
    collection: FS.experiencesDenormalized,
    data
  };
};

const isSubTaskDone = (s: ISubTask | SubTaskDenormalized) => {
  return s.status === SubTaskStatus.DONE || s.status === SubTaskStatus.SKIPPED;
};

const isCriteriumFulfilled = (
  criterium: TaskTemplateCompletionCritertia,
  task: ITask | ITaskDenormalized
) => {
  const mandatorySubtasks = (task.subTasks as (
    | ISubTask
    | SubTaskDenormalized
  )[]).filter((subTask) => {
    return subTask.requirementType === RequirementType.MANDATORY;
  });
  switch (criterium.type) {
    case 'SUBSET':
      // A `minimumRequired: 0` SUBSET is vacuously true (0 >= 0) — it would
      // complete a task that still has pending MANDATORY subtasks and ZERO work
      // done (e.g. "Book kick-off meeting"). Require at least 1 so a minReq:0
      // criterium is NOT a completion shortcut; the task then completes only via
      // the all-mandatory-done fallback in isTaskDone. Mirrors the backend
      // (new-main taskStatus.ts). Deliberate, documented divergence from v1.
      return (
        criterium.minimumRequired >= 1 &&
        mandatorySubtasks.filter(isSubTaskDone).length >=
          criterium.minimumRequired
      );
    default:
      return assertNever(criterium.type);
  }
};

export const areAllTasksDone = (tasks: (ITask | ITaskDenormalized)[]) => {
  return tasks.every((t) => t.status === TaskStatus.COMPLETE);
};

export const areAnyParticipantTasksLate = (tasks: ITask[]) => {
  return tasks.some(
    (t) => t.actor === PARTICIPANT_ACTOR_KEY && t.status === TaskStatus.LATE
  );
};

// const __isTaskDone = (
//   subTasksByTplId: { [tplId: string]: ISubTask | SubTaskDenormalized },
//   completionCriteria: TaskTemplateCompletionCritertia[]
// ) => {
//   const tester = { ...subTasksByTplId };
//   for (const criterium of completionCriteria) {
//     const subTasks = compact(
//       criterium.subTasks.map((sTplId) => tester[sTplId])
//     ).filter((s) => s.requirementType === RequirementType.MANDATORY);

//     const diff = Math.max(criterium.subTasks.length - subTasks.length, 0);
//     if (
//       !isCriteriumFulfilled(
//         {
//           ...criterium,
//           minimumRequired: criterium.minimumRequired - diff
//         },
//         subTasks
//       )
//     ) {
//       return false;
//     }
//     subTasks.forEach((s) => delete tester[s.templateId]);
//   }
//   const remainingSubTasks = Object.values(tester);
//   return remainingSubTasks.every(isSubTaskDone);
// };

export const isTaskSkipped = (task: ITask) => {
  return task.subTasks.every((sub) => sub.status === SubTaskStatus.SKIPPED);
};

// export const isTaskDone = (tpl: ITaskTemplate, task: ITask) => {
//   const mandatorySubtasks = task.subTasks.filter(
//     (st) => st.requirementType === RequirementType.MANDATORY
//   );

//   if (mandatorySubtasks.length < 1) {
//     return task.subTasks.some((s) => isSubTaskDone(s));
//   }
//   const subTasksByTplId = keyBy(mandatorySubtasks, (s) => s.templateId);
//   const audience = getAudienceById(tpl.audiences, task.audience);
//   return __isTaskDone(subTasksByTplId, audience.completionCriteria);
// };

export const isTaskDone = (
  task: ITask | ITaskDenormalized,
  completionCriteria: TaskTemplateCompletionCritertia[],
  tpl?: ITaskTemplate
) => {
  const presentBlocks = new Set(tpl?.blocks.map((b) => b.id));

  const mandatorySubtasks = (task.subTasks as (
    | ISubTask
    | SubTaskDenormalized
  )[]).filter((subTask) => {
    if (
      tpl &&
      presentBlocks.size > 0 &&
      !presentBlocks.has(subTask.templateId)
    ) {
      return false;
    }

    return subTask.requirementType === RequirementType.MANDATORY;
  });
  if (mandatorySubtasks.length < 1) {
    return task.subTasks.some((s) => isSubTaskDone(s));
  }

  for (const criterium of completionCriteria) {
    const minimumRequired = Math.min(
      criterium.minimumRequired,
      mandatorySubtasks.length
    );
    if (isCriteriumFulfilled({ ...criterium, minimumRequired }, task)) {
      return true;
    }
  }

  return mandatorySubtasks.every(isSubTaskDone);
};

export const isTaskDenormalizedDone = (task: ITaskDenormalized) => {
  return isTaskDone(task, task.customCompletionCriteria);
};

/**
 * This is deprecated but seems to be exactly what we need to check if
 * subtasks skipped should trigger effects
 * @deprecated This will be done in batch in the future
 */
export const completeSubTaskAndUpdate = async (
  tpl: Doc<IExperienceTemplate>,
  originalExperience: Doc<IExperience>,
  taskId: string,
  subTaskId: string,
  initiator: string,
  completionStatus: SubTaskStatus.DONE | SubTaskStatus.SKIPPED
) => {
  return callFirebaseFunction<string>(
    'experiences-completeSubTaskFromAdminApp',
    {
      completionChannel: EventChannel.APP,
      completionStatus,
      experienceId: originalExperience.id,
      subTaskId,
      initiator,
      taskId,
      templateId: tpl.id
    }
  );
};

export const completeTaskBySkippingRemainingSubTasks = async (
  tpl: Doc<IExperienceTemplate>,
  originalExperience: Doc<IExperience>,
  taskId: string,
  subTaskIds: string[],
  initiator: string
) => {
  return callFirebaseFunction<string>(
    'experiences-skipRemainingSubTasksFromAdminApp',
    {
      completionChannel: EventChannel.APP,
      experienceId: originalExperience.id,
      subTaskIds,
      initiator,
      taskId,
      templateId: tpl.id
    }
  );
};

export const reopenAutomation = (
  d: Doc<IExperience>,
  automationTplId: string
): Doc<IExperience> => {
  const { automations } = d.data;
  const automation = automations[automationTplId];
  if (!automation) {
    console.warn('Automation not found', { d, automationTplId });
    throw new Error('AUTOMATION_TPL_NOT_FOUND');
  }
  Object.values(automation.actions).forEach((a) => {
    a.data = null;
    a.errorData = null;
    a.status = AutomationActionStatus.UPCOMING;
  });
  const nextActions = fromPairs(
    Object.entries(automation.actions).map(([k, a]) => {
      return [
        k,
        {
          ...a,
          data: null,
          errorData: null,
          statu: AutomationActionStatus.UPCOMING
        }
      ];
    })
  );
  automation.completedAt = null;
  automation.status = AutomationStatus.UPCOMING;
  return {
    ...d,
    data: {
      ...d.data,
      automations: {
        ...automations,
        [automationTplId]: {
          ...automation,
          actions: nextActions
        }
      }
    }
  };
};

export const forceRunAutomationActions = (
  orgId: string,
  args: {
    experienceTplId: string;
    automationTplId: string;
    actionTplId: string;
    instanceId: string;
  }[],
  ts: number
) => {
  return callFirebaseFunction('automationSchedules-forceRunAutomationActions', {
    orgId,
    args,
    ts
  });
};

export const skipActionsAndUpdate = async (
  instanceId: string,
  items: {
    automationTplId: string;
    actionTplIds: string[];
  }[],
  initiator: string,
  at: Timestamp
) => {
  await updateInTransaction<IExperience>(
    store().collection(FS.experiences).doc(instanceId),
    async (d) => {
      if (!d) {
        return null;
      }

      const { automations } = d;
      items.forEach(({ automationTplId, actionTplIds }) => {
        const automation = automations[automationTplId];
        if (!automation) {
          console.warn('Automation not found', { d, automationTplId });
          return null;
        }

        actionTplIds.forEach((actionTplId) => {
          const action = automation.actions[actionTplId];
          if (!action) {
            console.warn('Automation not found', {
              d,
              automation,
              actionTplId
            });
            return null;
          }
          action.status = AutomationActionStatus.SKIPPED;
        });

        const actionStatuses = Object.values(automation.actions).map(
          (a) => a.status
        );
        const prevStatus = automation.status;
        const nextStatus = actionStatuses.some(
          (s) => s === AutomationActionStatus.ERROR
        )
          ? AutomationStatus.ERROR
          : actionStatuses.every((s) => s === AutomationActionStatus.SKIPPED)
          ? AutomationStatus.SKIPPED
          : actionStatuses.every(
              (s) =>
                s === AutomationActionStatus.DONE ||
                s === AutomationActionStatus.SKIPPED
            )
          ? AutomationStatus.DONE
          : AutomationStatus.UPCOMING;

        if (
          prevStatus !== nextStatus &&
          (nextStatus === AutomationStatus.DONE ||
            nextStatus === AutomationStatus.ERROR)
        ) {
          automation.completedAt = at;
        }
        // TODO create events so that we track timings for further analysis
        automation.status = nextStatus;
      });
      return { automations };
    },
    normalizeExperience
  );
};

export const retryAssignSupportingActor = async ({
  errData,
  args,
  currentUserId
}: {
  errData: ExperienceProblemData;
  args: RetryAssigningSupportingActorItemArgs;
  currentUserId: string;
}) => {
  try {
    await callFirebaseFunction(
      'experiencesQuestionAnswers-retryAssignSupportingActor',
      args
    );

    await updateInTransaction<IExperience>(
      store().collection(FS.experiences).doc(args.expId),
      async (d) => {
        if (!d) {
          return null;
        }
        const { instanceErrors } = d;
        const nextErrors = instanceErrors.map((ie) => {
          if (ie.id === args.instErr.id) {
            return { ...ie, ignore: true };
          }
          return ie;
        });
        return { instanceErrors: nextErrors };
      },
      normalizeExperience
    );
  } catch (e) {
    console.error('Retry assigning error', e);
  }
};

export const retrySendDocusingEnvelope = async ({
  errData
}: {
  errData: ExperienceProblemData;
}) => {
  if (errData.type === ErroDataType.CANNOT_SEND_DOCUSIGN_ENVELOPE) {
    type Args = {
      orgId: string;
      tplId: string;
      expId: string;
      subTaskTemplateId: string;
      taskTemplateId: string;
      resourceId: string;
    };
    const args: Args = {
      orgId: errData.tpl.data.orgId,
      tplId: errData.tpl.id,
      expId: errData.instance.id,
      resourceId: errData.resourceId,
      subTaskTemplateId: errData.subtaskTplId,
      taskTemplateId: errData.taskTplId
    };
    await callFirebaseFunction(CF.docusign.retrySendDocusingEnvelope, args);

    await updateInTransaction<IExperience>(
      store().collection(FS.experiences).doc(args.expId),
      async (d) => {
        if (!d) {
          return null;
        }
        const { instanceErrors } = d;
        const nextErrors = instanceErrors.map((ie) => {
          if (ie.id === errData.id) {
            return { ...ie, ignore: true };
          }
          return ie;
        });
        return { instanceErrors: nextErrors };
      },
      normalizeExperience
    );
  }
};

export const resendExperienceItem = (args: ResendExperienceItemArgs) => {
  return callFirebaseFunction('schedules-resendExperienceItem', args);
};

// onRetry
export const retrySendingConcreteMessage = async ({
  orgId,
  experienceId,
  message,
  actor,
  channels
}: {
  orgId: string;
  experienceId: string;
  message: ConcreteExperienceMessage;
  actor: string;
  channels: ScheduleChannel[];
}) => {
  if (message.type !== 'EXPERIENCE_MESSAGE' && message.type !== 'TASK_START') {
    console.log(
      'Resending is only supported for experience messages and task starts'
    );
    return;
  }

  await resendExperienceItem(
    message.type === 'EXPERIENCE_MESSAGE'
      ? {
          orgId,
          experienceId,
          itemId: message.scope?.msgId || '',
          itemType: 'MESSAGE',
          actor,
          channels,
          includedTaskStartIds: message.scope?.taskIds || []
        }
      : {
          orgId,
          experienceId,
          itemIds: message.scope?.taskIds || [],
          itemType: 'TASK',
          actor,
          channels
        }
  )
    .catch((e) => {
      console.error('RESEND ERR: ', e);
      return;
    })
    .then(async () => {
      await updateInTransaction<IExperience>(
        store().collection(FS.experiences).doc(experienceId),
        async (d) => {
          if (!d) {
            return null;
          }
          const { messages } = d;
          const nextMessages = messages.map((m) => {
            if (m.msgId === message.msgId) {
              // no need to set the status - a new message is going to come in after
              // we resend it anyway. we just ignore the past error.
              return { ...m, ignore: true };
            }
            return m;
          });
          return { messages: nextMessages };
        },
        normalizeExperience
      );
    });
};

// On Ignore
export const ignoreSentItemIssues = async (
  instanceId: string,
  items: ConcreteExperienceMessage[],
  currentUserId: string
) => {
  // TODO simplify this and don't set the status to OK?
  await updateInTransaction<IExperience>(
    store().collection(FS.experiences).doc(instanceId),
    async (d) => {
      if (!d) {
        return null;
      }

      const { messages } = d;
      // because we've grouped them before
      const nextMessages = messages.map((m) => {
        if (!!items.find((x) => x.msgId === m.msgId)) {
          return { ...m, ignore: true };
        }
        return m;
      });

      // TODO mark message as skipped if need be
      // items.forEach((item) => {
      //   const issues = messages.filter((m) => {
      //     return (
      //       m.scope?.msgId === item.msgTplId &&
      //       m.provider === item.msg.provider &&
      //       m.error?.type === item.msg.error?.type
      //     );
      //   });
      //   issues.forEach((issue) => {
      //     issue.status = 'OK';
      //     issue.ignore = true;

      //     // TODO this doesn't work as the changes wouldn't be saved anyway
      //     const expMessage = messagesStatuses[item.msgTplId];
      //     if (expMessage) {
      //       expMessage.status = MessageStatus.SKIPPED;
      //     }
      //   });
      // });
      return { messages: nextMessages };
    },
    normalizeExperience
  );
};

export const ignoreMissingDataIssue = async (
  instanceId: string,
  issues: ExperienceProblemData[],
  currentUserId: string
) => {
  await updateInTransaction<IExperience>(
    store().collection(FS.experiences).doc(instanceId),
    async (d) => {
      if (!d) return null;
      const { ignoredMissingActors, ignoredVariables = [] } = d;

      issues.forEach((issue) => {
        if (issue.type === 'MISSING_ACTOR') {
          ignoredMissingActors[issue.actor.key] = {
            ignored: true,
            ignoredAt: now(),
            ignoredBy: currentUserId,
            key: issue.actor.key
          };
        }
        if (issue.type === 'MISSING_VAR') {
          issue.missingVar.forEach((missing) => {
            const ignored = ignoredVariables.some((item) => item === missing);
            !ignored && ignoredVariables.push(missing);
          });
        }
      });
      return { ignoredMissingActors, ignoredVariables };
    },
    normalizeExperience
  );
};

export const ignoreSupportingActorIssue = async ({
  instanceId,
  issues,
  currentUserId
}: {
  instanceId: string;
  issues: ExperienceProblemData[];
  currentUserId: string;
}) => {
  await updateInTransaction<IExperience>(
    store().collection(FS.experiences).doc(instanceId),
    async (d) => {
      if (!d) return null;
      const { instanceErrors } = d;

      issues.forEach((issue) => {
        const match = instanceErrors.find((iErr) => issue.id === iErr.id);
        if (match) {
          match.ignore = true;
        }
      });
      return { instanceErrors };
    },
    normalizeExperience
  );
};

export const ignoreDocusignIssue = async ({
  instanceId,
  issues,
  currentUserId
}: {
  instanceId: string;
  issues: ExperienceProblemData[];
  currentUserId: string;
}) => {
  await updateInTransaction<IExperience>(
    store().collection(FS.experiences).doc(instanceId),
    async (d) => {
      if (!d) return null;
      const { instanceErrors } = d;

      issues.forEach((issue) => {
        const match = instanceErrors.find((iErr) => issue.id === iErr.id);
        if (match) {
          match.ignore = true;
        }
      });
      return { instanceErrors };
    },
    normalizeExperience
  );
};

/**
 * Generic "flip instanceErrors[i].ignore = true" for InstanceError-typed
 * rows in the issues bar. Used for ASSIGNEE_BOUNCE and CC_SEND_ERROR which
 * previously had no ignore wiring at all — clicking Ignore was a silent
 * no-op. See bd new-main-1gz.
 *
 * The existing `ignoreSupportingActorIssue` and `ignoreDocusignIssue`
 * helpers do the same thing for their specific error types; this helper
 * is the generic shape so new InstanceError types don't each need a
 * dedicated handler.
 */
export const ignoreInstanceErrorIssues = async ({
  instanceId,
  issues
}: {
  instanceId: string;
  issues: ExperienceProblemData[];
}) => {
  await updateInTransaction<IExperience>(
    store().collection(FS.experiences).doc(instanceId),
    async (d) => {
      if (!d) return null;
      const { instanceErrors } = d;
      issues.forEach((issue) => {
        // A collapsed row (e.g. a Salesforce upsert that failed on every
        // worker retry, shown as one "×N" row) carries every underlying
        // entry id in `groupedErrorIds` — ignore them all. Fall back to the
        // single id for non-collapsed rows.
        const idsToIgnore =
          (issue as { groupedErrorIds?: string[] }).groupedErrorIds?.length
            ? (issue as { groupedErrorIds: string[] }).groupedErrorIds
            : [issue.id];
        const idSet = new Set(idsToIgnore);
        instanceErrors.forEach((iErr) => {
          if (idSet.has(iErr.id)) iErr.ignore = true;
        });
      });
      return { instanceErrors };
    },
    normalizeExperience
  );
};

/**
 * SC-33594: clear the ASSIGNEE_BOUNCED instanceError(s) backing a "…bounced"
 * issue row after a successful Retry. Retry already flips `message.ignore`
 * (see retrySendingConcreteMessage), but the ASSIGNEE_BOUNCE row is driven off
 * `instanceErrors[].ignore` — which nothing in the retry path was touching — so
 * the issue never disappeared even when the resend went through.
 *
 * We ignore ALL non-ignored ASSIGNEE_BOUNCED entries matching the same
 * recipient email + timeline item, not just the clicked one: a single bounced
 * send can be recorded as several duplicate rows (one per redelivered Postmark
 * bounce webhook), and one successful resend resolves deliverability for all of
 * them. `itemTplId` matches whatever the bounce entry stored (message tpl id
 * for MESSAGE, task instance id for TASK) — the retry passes the issue's own
 * `itemTplId`, so the comparison is against the same stored value.
 */
export const clearAssigneeBounceIssuesForItem = async ({
  instanceId,
  itemType,
  itemTplId,
  email
}: {
  instanceId: string;
  itemType: 'MESSAGE' | 'TASK';
  itemTplId: string;
  email: string;
}) => {
  await updateInTransaction<IExperience>(
    store().collection(FS.experiences).doc(instanceId),
    async (d) => {
      if (!d) return null;
      const { instanceErrors } = d;
      markMatchingAssigneeBouncesIgnored(instanceErrors, {
        itemType,
        itemTplId,
        email
      });
      return { instanceErrors };
    },
    normalizeExperience
  );
};

export const sendManualReminder = (d: Doc<IExperience>, actor: string) => {
  // get all late tasks
  const taskIds = d.data.tasks
    .filter((task) => task.actor === actor && task.status === TaskStatus.LATE)
    .map((task) => task.id);
  if (!taskIds.length) {
    throw new Error('NO_LATE_TASKS');
  }
  return callFirebaseFunction('experiences-sendManualLateReminder', {
    experienceId: d.id,
    actor,
    taskIds
  });
};

/**
 * Archives an experience instance.
 * This skips all upcoming items, removes schedules, and sets manuallyArchivedAt.
 */
export const archiveInstance = (instanceId: string) => {
  return callFirebaseFunction<{ success: boolean; archivedAt: string }>(
    'experiences_archiveInstance',
    { instanceId }
  );
};

/**
 * Deletes an archived experience instance.
 * Requires the instance to be archived first (manuallyArchivedAt must be set).
 * Moves the instance to deletedExperiencesV1 for audit trail.
 */
export const deleteArchivedInstance = (instanceId: string, reason: string) => {
  return callFirebaseFunction<{ success: boolean; deletedAt: string }>(
    'experiences_deleteArchivedInstance',
    { instanceId, reason }
  );
};

/**
 * Check if an instance is auto-archived (past expectedEnd + expectedEndThreshold from template).
 * Used to determine if the Delete button should be shown directly (without archiving first).
 *
 * @param expectedEndThresholdMs - threshold in milliseconds
 */
export const isInstanceAutoArchived = (
  instance: IExperience,
  expectedEndThresholdMs: number
): boolean => {
  // Time-based archival only (manual archival is handled by the archive flow).
  // Uses the BE computed archive date (max(expectedEnd, createdAt) + threshold,
  // see getComputedArchiveDate) so a backdated / late enrollment isn't treated
  // as auto-archived the moment it's created.
  return tsIsAfter(now(), getComputedArchiveDate(instance, expectedEndThresholdMs));
};

export const removeInstance = (
  d: Doc<IExperience>,
  userId: string,
  orgId: string
) => {
  const result = removeDoc(d);
  const participantId =
    d.data.actors
      .filter(isExperienceUserActor)
      .find((a) => a.key === PARTICIPANT_ACTOR_KEY)?.userId || '';
  const removeInstanceAction: InstanceDeletedAction = {
    action: CUSTOMER_ACTIONS.INSTANCE_DELETED,
    adminUserId: userId,
    instanceId: d.id,
    templateId: d.data.templateId,
    participantId,
    orgId
  };
  tryReportCustomerAction(removeInstanceAction);

  return result;
};

const eventsByInstanceIdStore = createCollectionListenerStore(
  (compositeKey: string) => {
    const [orgId, instanceId] = JSON.parse(compositeKey);
    return new CollectionListener(
      store()
        .collection(FS.events)
        .where('orgId', '==', orgId)
        .where('expId', '==', instanceId),
      toEventDoc
    );
  }
);

export const useInstanceEvents = (orgId: string, instanceId: string) => {
  return useCollectionListener(
    eventsByInstanceIdStore(JSON.stringify([orgId, instanceId]))
  );
};

export const isExperienceUserActor = (
  expActor: ExperienceActor
): expActor is ExperienceUserActor => {
  return expActor.type === ExperienceActorType.USER;
};

export const getActorForKey = (d: Doc<IExperience>, actorKey: string) => {
  return d.data.actors.find((a) => a.key === actorKey) || null;
};

export const getUserActorForKey = (d: Doc<IExperience>, actorKey: string) => {
  return (
    d.data.actors
      .filter(isExperienceUserActor)
      .find((a) => a.key === actorKey) || null
  );
};

export const getParticipantActor = (d: Doc<IExperience>) => {
  return (
    d.data.actors
      .filter(isExperienceUserActor)
      .find((a) => a.key === PARTICIPANT_ACTOR_KEY) || null
  );
};

export const getAllActorUserIds = (d: Doc<IExperience>) => {
  return d.data.actors.filter(isExperienceUserActor).map((a) => a.userId);
};

export const useActorsForExperience = (
  d: Doc<IExperience>
): LoadingValue<{ [actorKey: string]: Doc<IUser> }> => {
  const { orgId, actors } = d.data;
  const userActors = actors.filter(isExperienceUserActor);
  const mapFn = useCallback(
    (users: { [key: string]: Doc<IUser> }) => {
      const allActors: { [actorKey: string]: Doc<IUser> } = {};
      for (const a of userActors) {
        allActors[a.key] = users[a.userId] || UNKNOWN_USER();
      }
      return allActors;
    },
    [userActors]
  );
  return useMappedLoadingValue(
    useUserMapById(
      orgId,
      userActors.map((a) => a.userId)
    ),
    mapFn,
    true
  );
};

export const getEarliestDateInInstance = (d: Doc<IExperience>) => {
  // Maybe now needs to be factored in here as well.
  // Need to test how an experience far in the future reacts
  // to changes. (one that has not started)
  // It might be worth considering whether a not started experience should
  // just be rebuilt from scratch without any checking.
  return (
    minBy(
      [
        d.data.startsAt,
        ...d.data.tasks.map((tsk) => tsk.timing.start),
        ...Object.values(d.data.automations).map((a) => a.timing.at),
        ...Object.values(d.data.messagesStatuses).map((m) => m.timing.at)
      ],
      (x) => x.toMillis()
    ) || d.data.startsAt
  );
};

export const updateActorForInstance = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  participant: Doc<IUser>,
  actorKey: string,
  nextUserId: string
) => {
  const n = now();
  const earliestDateInInstance = getEarliestDateInInstance(d);
  const prevActor = d.data.actors
    .filter(isExperienceUserActor)
    .find((a) => a.key === actorKey);
  if (!prevActor) {
    console.warn('ACTOR NOT FOUND', { d, actorKey });
    // this e.g. happens when no actor was assigned, most likely in error.
    const newActor: ExperienceUserActor = {
      type: ExperienceActorType.USER,
      userId: nextUserId,
      key: actorKey,
      accessToken: generateAccessToken()
    };
    const extendedActors = [...d.data.actors, newActor];
    const tasksToAdd = compact(
      tpl.data.tasks.map<ITask | null>((tTpl) => {
        console.log(tTpl.title);
        if (tTpl.actor !== actorKey) {
          return null;
        }

        if (isEventDrivenTiming(tTpl.timing)) {
          return null;
        }

        const dates = getFreshTaskDates(
          tTpl.timing,
          d.data.anchorDate.at,
          tpl.data.anchor,
          tpl.data.tz
        );
        if (tsIsAfter(n, dates.start)) {
          if (tsIsAfter(earliestDateInInstance, dates.start)) {
            const duration = dates.end.toMillis() - dates.start.toMillis();
            dates.start = earliestDateInInstance;
            dates.end = addToTs(earliestDateInInstance, duration);
          }
        }
        //     a     b
        //  n
        //        n       n
        // if (tsIsAfter(n, dates.start) && tsIsAfter(n, dates.end)) {
        //   // too old to add
        //   return null;
        // }
        const nextTask = createNotStartedTask(
          d.id,
          tTpl,
          participant,
          dates,
          d.data.anchorDate.at
        );

        if (
          nextTask &&
          skipAllBecauseCatchupModeIsDisabled(tTpl, n, nextTask.timing.start)
        ) {
          nextTask.subTasks.forEach((s) => {
            s.status = SubTaskStatus.SKIPPED;
          });
          nextTask.status = TaskStatus.SKIPPED;
        }

        return nextTask;
      })
    );
    const nextTasks = [...d.data.tasks, ...tasksToAdd];

    const nextMessageStatuses = { ...d.data.messagesStatuses };
    getAllMessageTemplates(tpl.data.messages).forEach((m) => {
      if (m.actor !== actorKey) {
        return null;
      }
      if (isEventDrivenTiming(m.timing)) {
        return null;
      }
      const at = getFreshNonTaskStartDate(
        m.timing,
        d.data.anchorDate.at,
        tpl.data.anchor,
        tpl.data.tz
      );

      const finalDate =
        tsIsAfter(n, at) && tsIsAfter(earliestDateInInstance, at)
          ? earliestDateInInstance
          : at;
      const nextMsgStatus = createNotStartedMessageStatus(
        m,
        participant,
        finalDate,
        d.data.anchorDate.at
      );
      if (nextMsgStatus) {
        if (
          skipAllBecauseCatchupModeIsDisabled(m, n, nextMsgStatus.timing.at)
        ) {
          nextMsgStatus.status = MessageStatus.SKIPPED;
        }
        nextMessageStatuses[m.id] = nextMsgStatus;
      }
      return;
    });
    const nextInstance = updateDataInDoc(d, {
      tasks: nextTasks,
      messagesStatuses: nextMessageStatuses,
      actors: extendedActors,
      userIds: extendedActors
        .filter(isExperienceUserActor)
        .map((a) => a.userId),
      accessTokens: extendedActors.map((a) => a.accessToken)
    });
    nextInstance.data.expectedEnd = computeExpectedEndForInstance(d);
    return nextInstance;
  }
  const nextActors = d.data.actors.slice();
  const i = nextActors.indexOf(prevActor);
  nextActors[i] = { ...prevActor, userId: nextUserId };
  const nextUsersIds = nextActors
    .filter(isExperienceUserActor)
    .map((a) => a.userId);
  return updateDataInDoc(d, {
    actors: nextActors,
    userIds: nextUsersIds
  });
};

export const repositionAnchorForInstance = (
  d: Doc<IExperience>,
  tpl: Doc<IExperienceTemplate>,
  participant: Doc<IUser>,
  nextAnchorDate: Timestamp,
  n: Timestamp,
  updateReason: InstanceRecalculateReason,
  opts: {
    overrideStartsAtDate?: boolean;
    honorCatchupModeSettings?: boolean;
  } = {}
): Doc<IExperience> => {
  const instanceWithUpdatedAnchor = {
    ...d,
    data: {
      ...d.data,
      anchorDate: {
        at: nextAnchorDate
      }
    }
  };
  if (opts.overrideStartsAtDate) {
    const offset = getAnchorOffsetForExperience(tpl.data.anchor);
    instanceWithUpdatedAnchor.data.startsAt = addToTs(
      instanceWithUpdatedAnchor.data.anchorDate.at,
      -offset
    );
  }
  return __recalculateExperienceInstanceAtPointInTimeWithoutSideEffects(
    tpl,
    instanceWithUpdatedAnchor,
    participant,
    n,
    updateReason,
    { honorCatchupModeSettings: !!opts.honorCatchupModeSettings }
  );
};

export const moveSelectedExperienceItemsRelatively = (
  tpl: Doc<IExperienceTemplate>,
  exp: Doc<IExperience>,
  itemsToMove: {
    tasks: { [taskId: string]: boolean };
    automations: { [aId: string]: boolean };
    messages: { [msgId: string]: boolean };
  },
  msToMove: number,
  currentTime: Timestamp
) => {
  const nextTasks = exp.data.tasks.map((t) => {
    if (itemsToMove.tasks[t.templateId]) {
      const taskTpl = getTaskTemplateById(tpl, t.templateId);
      if (!taskTpl) {
        console.log('Task template not found');
        return t;
      }
      const nextTiming = {
        start: addToTs(t.timing.start, msToMove),
        end: addToTs(t.timing.end, msToMove)
      };
      const nextTask: ITask = {
        ...t,
        timing: {
          ...nextTiming,
          manualOverride: !isStandardTimingForTask(
            tpl,
            exp,
            t.templateId,
            nextTiming
          )
        }
      };
      nextTask.status = computeTaskStatusButKeepNotStartedState(
        taskTpl,
        nextTask,
        currentTime
      );
      return nextTask;
    }
    return t;
  });

  const nextAutomations = mapValues(exp.data.automations, (v) => {
    if (itemsToMove.automations[v.templateId]) {
      const nextAt = addToTs(v.timing.at, msToMove);
      const isUpcoming = tsIsAfter(nextAt, currentTime);
      const nextAutomation: IAutomation = {
        ...v,
        actions: mapValues(v.actions, (action) =>
          isUpcoming && action.status !== AutomationActionStatus.SKIPPED
            ? {
                ...action,
                data: null,
                errorData: null,
                doneAt: null,
                status: AutomationActionStatus.UPCOMING
              }
            : action
        ),
        timing: {
          at: nextAt,
          manualOverride: !isStandardTimingForAutomation(
            tpl,
            exp,
            v.templateId,
            nextAt
          )
        }
      };
      nextAutomation.status = computeAutomationStatus(nextAutomation);
      nextAutomation.completedAt =
        nextAutomation.status === AutomationStatus.DONE
          ? nextAutomation.completedAt
          : null;
      return nextAutomation;
    }
    return v;
  });

  const nextMessageStatuses = mapValues(exp.data.messagesStatuses, (m, mId) => {
    if (itemsToMove.messages[mId]) {
      const nextAt = addToTs(m.timing.at, msToMove);
      const isUpcoming = tsIsAfter(nextAt, currentTime);
      return {
        ...m,
        status:
          isUpcoming && m.status !== MessageStatus.SKIPPED
            ? MessageStatus.UPCOMING
            : m.status,
        timing: {
          at: nextAt,
          manualOverride: !isStandardTimingForMessage(tpl, exp, mId, nextAt)
        }
      };
    }
    return m;
  });

  const nextInstance = updateDataInDoc(exp, {
    tasks: nextTasks,
    automations: nextAutomations,
    messagesStatuses: nextMessageStatuses
  });
  nextInstance.data.expectedEnd = computeExpectedEndForInstance(nextInstance);
  return nextInstance;
};

export const recalculateExperienceInstanceAtPointInTime = (
  instanceId: string
) => {
  return callFirebaseFunction(
    CF.experiences.recalculateExperienceInstanceAtPointInTime,
    {
      instanceId
    }
  );
};

export const updateInstanceSchedules = (instanceId: string) => {
  return callFirebaseFunction(CF.experiences.updateInstanceSchedules, {
    instanceId
  });
};

export const getExperiencesByIdAsMap = (ids: string[]) => {
  return getAllByIdAsMap(
    store().collection(FS.experiences),
    ids,
    toExperienceDoc
  );
};

const updateTaskInInstance = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  taskId: string,
  updateFn: (taskTpl: ITaskTemplate, task: ITask) => ITask
) => {
  const nextTasks = d.data.tasks.map((task) => {
    if (task.id === taskId) {
      const taskTpl = getTaskTemplateById(tpl, task.templateId);
      if (!taskTpl) {
        console.log('TASK TPL NOT FOUND');
        return task;
      }
      return updateFn(taskTpl, task);
    }
    return task;
  });
  return updateDataInDoc(d, { tasks: nextTasks });
};

const changeSubTaskStatus = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  taskId: string,
  subTaskId: string,
  nextStatus: SubTaskStatus
): Doc<IExperience> => {
  const n = now();
  return updateTaskInInstance(tpl, d, taskId, (taskTpl, task) => {
    const nextSubTasks = task.subTasks.map<ISubTask>((s) => {
      if (s.id === subTaskId) {
        return {
          ...s,
          status: nextStatus,
          completedAt:
            nextStatus === SubTaskStatus.DONE ||
            nextStatus === SubTaskStatus.SKIPPED
              ? n
              : s.completedAt || null
        };
      }
      return s;
    });
    const nextTask: ITask = {
      ...task,
      subTasks: nextSubTasks
    };
    nextTask.status = computeTaskStatus(taskTpl, nextTask, now());
    return nextTask;
  });
};

export const preSkipItem = (
  d: Doc<IExperience>,
  type: 'SUB_TASK' | 'MSG' | 'AUTOMATION_ACTION',
  id: string
) => {
  return updateDataInDoc(d, {
    preSkips: uniqBy(
      [...(d.data.preSkips || []), { type, id: id }],
      (x) => `${x.type}-${x.id}`
    )
  });
};

export const preUnskipItem = (
  d: Doc<IExperience>,
  type: 'SUB_TASK' | 'MSG' | 'AUTOMATION_ACTION',
  id: string
) => {
  return updateDataInDoc(d, {
    preSkips: (d.data.preSkips || []).filter(
      (x) => !(x.type === type && x.id === id)
    )
  });
};

export const checkIfLastSubTask = (
  task: ITaskDenormalized,
  subTaskId: string
): boolean => {
  if (!task) return false;

  const openSubTasks = task.subTasks.filter(
    (s) => s.status === SubTaskStatus.OPEN
  );

  return openSubTasks.length === 1 && openSubTasks[0].id === subTaskId;
};

export const skipSubTask = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  taskId: string,
  subTaskId: string
): Doc<IExperience> => {
  return changeSubTaskStatus(tpl, d, taskId, subTaskId, SubTaskStatus.SKIPPED);
};

export const skipSubTaskAndTrigger = async (
  tpl: Doc<IExperienceTemplate>,
  originalD: Doc<IExperience>, // Original state from DB
  currentD: Doc<IExperience>, // Current state with local changes
  taskId: string,
  subTaskId: string
): Promise<void> => {
  const updatedCurrentDoc = changeSubTaskStatus(
    tpl,
    currentD,
    taskId,
    subTaskId,
    SubTaskStatus.SKIPPED
  );

  const originalTask = originalD.data.tasks.find((t) => t.id === taskId);
  const currentTask = updatedCurrentDoc.data.tasks.find((t) => t.id === taskId);

  if (!originalTask || !currentTask) {
    throw new Error('Task not found');
  }

  // Collect all subtasks that were skipped locally but not saved to DB
  const subTasksToSkip: string[] = [];

  currentTask.subTasks.forEach((currentSubTask) => {
    const originalSubTask = originalTask.subTasks.find(
      (s) => s.id === currentSubTask.id
    );

    if (
      originalSubTask?.status === SubTaskStatus.OPEN &&
      currentSubTask.status === SubTaskStatus.SKIPPED
    ) {
      subTasksToSkip.push(currentSubTask.id);
    }
  });

  try {
    await completeTaskBySkippingRemainingSubTasks(
      tpl,
      originalD,
      taskId,
      subTasksToSkip,
      '' // initiator
    );
  } catch (error) {
    console.error('Failed to skip remaining subtasks on backend:', error);
    throw error;
  }

  return;
};

export const openSubTask = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  taskId: string,
  subTaskId: string
): Doc<IExperience> => {
  return changeSubTaskStatus(tpl, d, taskId, subTaskId, SubTaskStatus.OPEN);
};

export const skipTask = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  taskId: string
): Doc<IExperience> => {
  const n = now();
  return updateTaskInInstance(tpl, d, taskId, (taskTpl, task) => {
    const nextSubTasks = task.subTasks.map((s) => {
      if (s.status === SubTaskStatus.OPEN) {
        return {
          ...s,
          status: SubTaskStatus.SKIPPED,
          completedAt: n
        };
      }
      return s;
    });
    const nextTask: ITask = {
      ...task,
      subTasks: nextSubTasks
    };
    nextTask.status = computeTaskStatus(taskTpl, nextTask, n);
    return nextTask;
  });
};

export const unskipTask = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  taskId: string
): Doc<IExperience> => {
  return updateTaskInInstance(tpl, d, taskId, (taskTpl, task) => {
    const nextSubTasks = task.subTasks.map((s) => {
      if (s.status === SubTaskStatus.SKIPPED) {
        return {
          ...s,
          status: SubTaskStatus.OPEN
        };
      }
      return s;
    });
    const nextTask: ITask = {
      ...task,
      subTasks: nextSubTasks
    };
    nextTask.status = computeTaskStatus(taskTpl, nextTask, now());
    return nextTask;
  });
};

export const moveTaskToToday = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  taskId: string
) => {
  return updateTaskInInstance(tpl, d, taskId, (taskTpl, task) => {
    const today = moment().tz(tpl.data.tz);
    const duration = task.timing.end.toMillis() - task.timing.start.toMillis();
    const startM = toMoment(task.timing.start).tz(tpl.data.tz);
    startM.set({
      year: today.year(),
      month: today.month(),
      date: today.date()
    });
    const start = fromMoment(startM);
    const end = addToTs(start, duration);
    return {
      ...task,
      timing: {
        ...task.timing,
        start,
        end,
        manualOverride: !isStandardTimingForTask(tpl, d, taskTpl.id, {
          start,
          end
        })
      }
    };
  });
};

export const skipAutomation = (
  d: Doc<IExperience>,
  automationTplId: string
): Doc<IExperience> => {
  if (!d.data.automations[automationTplId]) {
    console.log('Automation not found');
    return d;
  }
  const nextAutomation: IAutomation = {
    ...d.data.automations[automationTplId]
  };
  nextAutomation.actions = mapValues(nextAutomation.actions, (ac) => {
    if (ac.status !== AutomationActionStatus.DONE) {
      return {
        ...ac,
        status: AutomationActionStatus.SKIPPED
      };
    }
    return ac;
  });
  nextAutomation.status = computeAutomationStatus(nextAutomation);
  return updateDataInDoc(d, {
    automations: {
      ...d.data.automations,
      [automationTplId]: nextAutomation
    }
  });
};

export const unskipAutomation = (
  d: Doc<IExperience>,
  automationId: string
): Doc<IExperience> => {
  if (!d.data.automations[automationId]) {
    console.log('Automation not found');
    return d;
  }
  const nextAutomation: IAutomation = { ...d.data.automations[automationId] };
  nextAutomation.actions = mapValues(nextAutomation.actions, (ac) => {
    if (ac.status === AutomationActionStatus.SKIPPED) {
      return {
        ...ac,
        status: AutomationActionStatus.UPCOMING
      };
    }
    return ac;
  });
  nextAutomation.status = computeAutomationStatus(nextAutomation);
  return updateDataInDoc(d, {
    automations: {
      ...d.data.automations,
      [automationId]: nextAutomation
    }
  });
};

export const moveAutomationToToday = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  automationTplId: string
): Doc<IExperience> => {
  if (!d.data.automations[automationTplId]) {
    console.log('Automation not found');
    return d;
  }
  const automation = d.data.automations[automationTplId];

  const { tz } = tpl.data;
  const today = moment().tz(tz);
  const runM = toMoment(automation.timing.at).tz(tz);
  runM.set('date', today.date());
  runM.set('month', today.month());
  runM.set('year', today.year());
  const at = fromMoment(runM);
  return updateDataInDoc(d, {
    automations: {
      ...d.data.automations,
      [automationTplId]: {
        ...automation,
        timing: {
          ...automation.timing,
          at,
          manualOverride: !isStandardTimingForAutomation(
            tpl,
            d,
            automationTplId,
            at
          )
        }
      }
    }
  });
};

const changeAutomationActionStatus = (
  d: Doc<IExperience>,
  automationTplId: string,
  actionId: string,
  nextStatus: AutomationActionStatus
): Doc<IExperience> => {
  if (!d.data.automations[automationTplId]?.actions[actionId]) {
    console.log('Action not found');
    return d;
  }

  const nextAutomation: IAutomation = {
    ...d.data.automations[automationTplId]
  };
  nextAutomation.actions = {
    ...nextAutomation.actions,
    [actionId]: {
      ...nextAutomation.actions[actionId],
      status: nextStatus
    }
  };

  nextAutomation.status = computeAutomationStatus(nextAutomation);
  return updateDataInDoc(d, {
    automations: {
      ...d.data.automations,
      [automationTplId]: nextAutomation
    }
  });
};

export const skipAutomationAction = (
  d: Doc<IExperience>,
  automationTplId: string,
  actionId: string
) =>
  changeAutomationActionStatus(
    d,
    automationTplId,
    actionId,
    AutomationActionStatus.SKIPPED
  );

export const unskipAutomationAction = (
  d: Doc<IExperience>,
  automationTplId: string,
  actionId: string
) =>
  changeAutomationActionStatus(
    d,
    automationTplId,
    actionId,
    AutomationActionStatus.UPCOMING
  );

const updateMessageInInstance = (
  d: Doc<IExperience>,
  msgId: string,
  updateFn: (msg: {
    status: MessageStatus;
    audience: string;
    overrides: {
      at: Timestamp | null;
    };
    timing: {
      at: Timestamp;
      manualOverride: boolean;
      impossibleToDetermine?: boolean; // this means that at is the start date of the experience. this happened when this field was introduced and we were unable to determine the date during migration as the template for it was already deleted
    };
  }) => Partial<{
    status: MessageStatus;
    audience: string;
    overrides: {
      at: Timestamp | null;
    };
    timing: {
      at: Timestamp;
      manualOverride: boolean;
      impossibleToDetermine?: boolean; // this means that at is the start date of the experience. this happened when this field was introduced and we were unable to determine the date during migration as the template for it was already deleted
    };
  }>
): Doc<IExperience> => {
  const msg = d.data.messagesStatuses[msgId];
  if (!msg) {
    return d;
  }
  const updates = updateFn(msg);
  return updateDataInDoc(d, {
    messagesStatuses: {
      ...d.data.messagesStatuses,
      [msgId]: {
        ...msg,
        ...updates
      }
    }
  });
};

export const skipMessage = (
  d: Doc<IExperience>,
  msgId: string
): Doc<IExperience> => {
  return updateMessageInInstance(d, msgId, () => ({
    status: MessageStatus.SKIPPED
  }));
};

export const unskipMessage = (
  d: Doc<IExperience>,
  msgId: string
): Doc<IExperience> => {
  return updateMessageInInstance(d, msgId, () => ({
    status: MessageStatus.UPCOMING
  }));
};

export const moveMessageToToday = (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  msgId: string
) => {
  return updateMessageInInstance(d, msgId, (msg) => {
    const { tz } = tpl.data;
    const today = moment().tz(tz);
    const sendM = toMoment(msg.timing.at).tz(tz);
    sendM.set('date', today.date());
    sendM.set('month', today.month());
    sendM.set('year', today.year());
    const at = fromMoment(sendM);
    return {
      ...msg,
      timing: {
        ...msg.timing,
        at,
        manualOverride: !isStandardTimingForMessage(tpl, d, msgId, at)
      }
    };
  });
};

export const getTaskByTaskTemplateId = (
  d: Doc<IExperience>,
  taskTplId: string
) => {
  return d.data.tasks.find((task) => task.templateId === taskTplId) || null;
};

/**
 * @deprecated
 */
export const getTaskCompletionEventByTaskId = (
  events: IExperience['events'],
  taskId: string
) => {
  return (
    events.find(
      (e): e is IExperienceEventTaskDone =>
        e.type === ExperienceEventType.TASK_DONE && e.taskId === taskId
    ) || null
  );
};

/**
 * @deprecated
 */
export const getTaskCompletionEventByTaskTemplateId = (
  d: Doc<IExperience>,
  taskTplId: string
) => {
  const task = getTaskByTaskTemplateId(d, taskTplId);
  return task
    ? getTaskCompletionEventByTaskId(d.data.events, task.id) || null
    : null;
};

export const groupConcreteMessages = (
  ds: ConcreteExperienceMessage[]
): {
  byMessage: { [msgTplId: string]: ConcreteExperienceMessage[] };
  byTask: { [taskTplId: string]: ConcreteExperienceMessage[] };
} => {
  const res: {
    byMessage: { [msgId: string]: ConcreteExperienceMessage[] };
    byTask: { [taskId: string]: ConcreteExperienceMessage[] };
  } = { byMessage: {}, byTask: {} };

  ds.forEach((d) => {
    if (d.scope?.msgId) {
      const msgContainer = (res.byMessage[d.scope.msgId] =
        res.byMessage[d.scope.msgId] || []);
      msgContainer.push(d);
    }

    if (d.scope?.taskIds) {
      d.scope.taskIds.forEach((taskId) => {
        const taskContainer = (res.byTask[taskId] = res.byTask[taskId] || []);
        taskContainer.push(d);
      });
    }
  });

  return res;
};
